function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

const togglerLoader = (flag, selector) => {
  const loader = selector ? document.querySelector(selector) : document.querySelector('[data-loader]');
  if (!loader) return;
  if (flag) return loader.classList.remove('hidden');
  loader.classList.add('hidden');
}

/** Combined listings (Shopify Plus): abort in-flight shell swap on rapid clicks */
let surgeCombinedListingAbort = null;

/**
 * Increments on every new combined-listing swap attempt. `requestAnimationFrame` tails
 * from an older swap compare against this so they no-op without clearing `surge-cl-shell--busy`
 * (which would belong to a newer in-flight swap).
 */
let surgeClShellSwapGen = 0;

/** Block variant→featured-media gallery jumps while combined-listing shell restores size/style. */
let surgeClGallerySyncSuppressedUntil = 0;

function surgeSuppressGallerySyncForCombinedListing(ms = 600) {
  surgeClGallerySyncSuppressedUntil = Math.max(
    surgeClGallerySyncSuppressedUntil,
    Date.now() + ms
  );
}

function surgeIsGallerySyncSuppressed() {
  return Date.now() < surgeClGallerySyncSuppressedUntil;
}

/** LRU cache of section HTML for sibling shells (same payload as swap fetch; avoids repeat network). */
const SURGE_CL_SECTION_CACHE_MAX = 12;
/** @type {Map<string, string>} */
const surgeClSectionHtmlCache = new Map();
/** @type {Map<string, Promise<string>>} */
const surgeClSectionInflight = new Map();
/** @type {Map<string, number>} */
const surgeClPrefetchPointerTs = new Map();
let surgeClPrefetchPointerBound = false;

/** LRU cache for `GET /products/{handle}.js` (canonical variant JSON for v1 combined-listing overlay). */
const SURGE_CL_PRODUCT_JS_CACHE_MAX = 24;
/** @type {Map<string, object>} */
const surgeClProductJsCache = new Map();
/** @type {Map<string, Promise<object>>} */
const surgeClProductJsInflight = new Map();

function surgeClProductJsCacheKey(targetPath) {
  return String(targetPath || '')
    .trim()
    .replace(/\/+$/, '');
}

function surgeClProductJsCacheGet(key) {
  const v = surgeClProductJsCache.get(key);
  if (v === undefined) return null;
  surgeClProductJsCache.delete(key);
  surgeClProductJsCache.set(key, v);
  return v;
}

function surgeClProductJsCacheSet(key, obj) {
  if (surgeClProductJsCache.has(key)) surgeClProductJsCache.delete(key);
  surgeClProductJsCache.set(key, obj);
  while (surgeClProductJsCache.size > SURGE_CL_PRODUCT_JS_CACHE_MAX) {
    const k0 = surgeClProductJsCache.keys().next().value;
    surgeClProductJsCache.delete(k0);
  }
}

/**
 * Storefront product JSON (`/products/{handle}.js`). Cached; dedupes in-flight like section HTML.
 * Non-OK / parse failures return `null`. `AbortError` propagates so callers can bail with section fetch.
 *
 * @param {string} targetPath e.g. `/products/my-handle`
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<object | null>}
 */
async function surgeGetStorefrontProductJson(targetPath, signal) {
  const key = surgeClProductJsCacheKey(targetPath);
  if (!key) return null;
  const hit = surgeClProductJsCacheGet(key);
  if (hit) return hit;

  let p = surgeClProductJsInflight.get(key);
  if (!p) {
    const fetchUrl = `${key}.js`;
    /** @type {RequestInit} */
    const fetchOpts = { headers: { Accept: 'application/json' } };
    if (signal) fetchOpts.signal = signal;
    p = fetch(fetchUrl, fetchOpts)
      .then(async (res) => {
        if (!res.ok) throw new Error(`product.js fetch failed: ${res.status}`);
        return res.json();
      })
      .then((obj) => {
        if (obj && typeof obj === 'object') surgeClProductJsCacheSet(key, obj);
        return obj;
      })
      .finally(() => {
        surgeClProductJsInflight.delete(key);
      });
    surgeClProductJsInflight.set(key, p);
  }
  try {
    return await p;
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    return null;
  }
}

function surgeCombinedListingShellCacheKey(targetPath, sectionId, optionValueId) {
  return `${targetPath}§${sectionId}§${String(optionValueId)}`;
}

function surgeClSectionCacheGet(key) {
  const v = surgeClSectionHtmlCache.get(key);
  if (v === undefined) return null;
  surgeClSectionHtmlCache.delete(key);
  surgeClSectionHtmlCache.set(key, v);
  return v;
}

function surgeClSectionCacheSet(key, html) {
  if (surgeClSectionHtmlCache.has(key)) surgeClSectionHtmlCache.delete(key);
  surgeClSectionHtmlCache.set(key, html);
  while (surgeClSectionHtmlCache.size > SURGE_CL_SECTION_CACHE_MAX) {
    const k0 = surgeClSectionHtmlCache.keys().next().value;
    surgeClSectionHtmlCache.delete(k0);
  }
}

/** Decode first gallery images from prefetched section HTML (reduces pop-in on swap). */
const SURGE_CL_GALLERY_WARM_MAX = 8;
const SURGE_CL_GALLERY_WARM_SEEN_MAX = 400;
/** @type {Set<string>} */
const surgeClGalleryWarmSeen = new Set();

function surgeClGalleryWarmSeenTrim() {
  if (surgeClGalleryWarmSeen.size <= SURGE_CL_GALLERY_WARM_SEEN_MAX) return;
  surgeClGalleryWarmSeen.clear();
}

function surgeWarmGalleryImagesFromSectionHtml(html) {
  if (!html || typeof html !== 'string') return;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const shell = doc.getElementById('surge-combined-listing-shell');
    const root = shell?.querySelector('.gallery-column') || shell;
    if (!root) return;
    /** @type {string[]} */
    const urls = [];
    const localSeen = new Set();
    const take = (/** @type {string | null} */ u) => {
      if (!u || urls.length >= SURGE_CL_GALLERY_WARM_MAX) return;
      const s = String(u).trim();
      if (!s || s.startsWith('data:') || localSeen.has(s)) return;
      localSeen.add(s);
      urls.push(s);
    };
    root.querySelectorAll('img[src], img[srcset]').forEach((img) => {
      take(img.getAttribute('src'));
      const ss = img.getAttribute('srcset');
      if (ss) {
        const first = ss.split(',')[0].trim().split(/\s+/)[0];
        take(first);
      }
    });
    surgeClGalleryWarmSeenTrim();
    urls.forEach((u) => {
      if (surgeClGalleryWarmSeen.has(u)) return;
      surgeClGalleryWarmSeen.add(u);
      const im = new Image();
      im.decoding = 'async';
      im.src = u;
    });
  } catch {
    /* ignore */
  }
}

/** Cap on holding the current gallery while incoming images decode; prefetched siblings hit ~0ms. */
const SURGE_CL_SWAP_DECODE_BUDGET_MS = 450;
const SURGE_CL_SWAP_DECODE_MAX_IMAGES = 3;

/**
 * Decode incoming above-the-fold gallery images off-DOM, before the live gallery is touched, so the
 * swap paints in one frame instead of inserting blank `<img>` boxes.
 *
 * @param {Element} nextShell parsed-but-not-yet-mounted section markup
 * @returns {Promise<boolean>} true when every candidate decoded inside the budget
 */
async function surgeDecodeIncomingGalleryImages(nextShell) {
  if (!nextShell) return true;

  /** @type {Element[]} */
  const candidates = [];
  const main =
    nextShell.querySelector('.product-gallery-main .splide__slide.is-active img') ||
    nextShell.querySelector('.product-gallery-main img');
  if (main) candidates.push(main);
  // Stacked desktop layout paints several images at once, so warm the first ones too.
  for (const img of nextShell.querySelectorAll('.gallery-stacked-desktop-only img')) {
    if (candidates.length >= SURGE_CL_SWAP_DECODE_MAX_IMAGES) break;
    if (!candidates.includes(img)) candidates.push(img);
  }
  if (!candidates.length) return true;

  let settled = 0;
  const jobs = candidates.map((img) => {
    const src = img.getAttribute('src') || '';
    const srcset = img.getAttribute('srcset') || '';
    if (!src && !srcset) {
      settled += 1;
      return Promise.resolve();
    }
    const pre = new Image();
    pre.decoding = 'async';
    const sizes = img.getAttribute('sizes');
    if (sizes) pre.sizes = sizes;
    if (srcset) pre.srcset = srcset;
    if (src) pre.src = src;
    const done = typeof pre.decode === 'function' ? pre.decode().catch(() => {}) : Promise.resolve();
    return done.then(() => {
      settled += 1;
    });
  });

  await Promise.race([
    Promise.all(jobs),
    new Promise((resolve) => setTimeout(resolve, SURGE_CL_SWAP_DECODE_BUDGET_MS))
  ]);
  return settled >= candidates.length;
}

function surgeWarmGalleryImagesFromSectionHtmlDeferred(html) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => surgeWarmGalleryImagesFromSectionHtml(html), { timeout: 2500 });
  } else {
    setTimeout(() => surgeWarmGalleryImagesFromSectionHtml(html), 0);
  }
}

/**
 * Fetch (or read cache) section rendering HTML for a combined-listing child.
 * Dedupes in-flight requests so prefetch + swap share one network call.
 *
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<{ html: string }>}
 */
async function surgeGetCombinedListingSectionHtml(targetPath, sectionId, optionValueId, signal) {
  const key = surgeCombinedListingShellCacheKey(targetPath, sectionId, optionValueId);
  const cached = surgeClSectionCacheGet(key);
  if (cached) {
    surgeWarmGalleryImagesFromSectionHtmlDeferred(cached);
    return { html: cached };
  }

  let p = surgeClSectionInflight.get(key);
  if (!p) {
    const qs = new URLSearchParams();
    qs.set('section_id', sectionId);
    qs.set('option_values', String(optionValueId));
    const fetchUrl = `${targetPath}?${qs.toString()}`;
    const fetchOpts = signal ? { signal } : {};
    p = fetch(fetchUrl, fetchOpts)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Combined listing section fetch failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        surgeClSectionCacheSet(key, html);
        surgeWarmGalleryImagesFromSectionHtmlDeferred(html);
        return html;
      })
      .finally(() => {
        surgeClSectionInflight.delete(key);
      });
    surgeClSectionInflight.set(key, p);
  }
  const html = await p;
  return { html };
}

/**
 * @returns {Array<{ siblingProductUrl: string, optionValueId: string }>}
 */
function surgeCollectCombinedListingSiblingPrefetchTasks(host) {
  /** @type {Array<{ siblingProductUrl: string, optionValueId: string }>} */
  const out = [];
  const seen = new Set();
  const push = (/** @type {string | null} */ url, /** @type {string | null} */ id) => {
    if (!url || !id || String(id).trim() === '') return;
    const path = surgeNormalizeProductPath(url);
    if (!path) return;
    const k = `${path}§${id}`;
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ siblingProductUrl: url, optionValueId: id });
  };
  const tag = host.tagName.toLowerCase();
  if (tag === 'variant-pills') {
    for (const inp of host.querySelectorAll('input[type="radio"]')) {
      const label = inp.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') continue;
      push(label.getAttribute('data-sibling-product-url'), label.getAttribute('data-option-value-id'));
    }
  } else if (tag === 'variant-dropdown') {
    for (const o of host.querySelectorAll('.option-value')) {
      push(o.getAttribute('data-sibling-product-url'), o.getAttribute('data-option-value-id'));
    }
  }
  return out;
}

/** Warm section HTML for other combined-listing children (idle + light staggering). */
function surgeScheduleCombinedListingSiblingPrefetch() {
  const host = document.querySelector(
    'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
  );
  const sectionId = host?.dataset?.sectionId;
  if (!host || !sectionId) return;

  const tasks = surgeCollectCombinedListingSiblingPrefetchTasks(host);
  if (!tasks.length) return;

  const runBatch = () => {
    let i = 0;
    const step = () => {
      if (i >= tasks.length) return;
      const t = tasks[i++];
      void surgePrefetchCombinedListingSection({ siblingProductUrl: t.siblingProductUrl, optionValueId: t.optionValueId, sectionId });
      setTimeout(step, 100);
    };
    step();
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(runBatch, { timeout: 4500 });
  } else {
    setTimeout(runBatch, 1200);
  }
}

async function surgePrefetchCombinedListingSection({ siblingProductUrl, optionValueId, sectionId }) {
  const targetPath = surgeNormalizeProductPath(siblingProductUrl);
  if (!targetPath || !sectionId || optionValueId == null || String(optionValueId).trim() === '') return;
  const key = surgeCombinedListingShellCacheKey(targetPath, sectionId, optionValueId);
  if (surgeClSectionHtmlCache.has(key) || surgeClSectionInflight.has(key)) {
    void surgeGetStorefrontProductJson(targetPath, undefined).catch(() => null);
    return;
  }
  try {
    await Promise.all([
      surgeGetCombinedListingSectionHtml(targetPath, sectionId, optionValueId, undefined),
      surgeGetStorefrontProductJson(targetPath, undefined).catch(() => null)
    ]);
  } catch {
    /* ignore prefetch errors */
  }
}

function surgeInitCombinedListingPrefetchPointer() {
  if (surgeClPrefetchPointerBound) return;
  surgeClPrefetchPointerBound = true;
  document.addEventListener(
    'pointerover',
    (e) => {
      const t = e.target;
      if (!t || !(t instanceof Element)) return;
      const el = t.closest('[data-sibling-product-url][data-option-value-id]');
      if (!el) return;
      const host = el.closest(
        'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
      );
      if (!host) return;
      const sectionId = host.dataset.sectionId;
      if (!sectionId) return;
      const url = el.getAttribute('data-sibling-product-url');
      const id = el.getAttribute('data-option-value-id');
      if (!url || !id) return;
      const path = surgeNormalizeProductPath(url);
      if (!path) return;
      const key = surgeCombinedListingShellCacheKey(path, sectionId, id);
      const now = Date.now();
      const last = surgeClPrefetchPointerTs.get(key) || 0;
      if (now - last < 500) return;
      surgeClPrefetchPointerTs.set(key, now);
      void surgePrefetchCombinedListingSection({ siblingProductUrl: url, optionValueId: id, sectionId });
    },
    { passive: true }
  );
}

/**
 * Fade gallery images in as they finish loading (helps when section HTML was cold on the network).
 * @param {Element | null | undefined} galleryRoot
 */
function surgeFadeGalleryImagesIn(galleryRoot) {
  if (!galleryRoot) return;
  let pending = 0;
  const imgs = [...galleryRoot.querySelectorAll('img')];
  imgs.forEach((img) => {
    if (img.complete && img.naturalWidth > 0) return;
    pending += 1;
    img.classList.add('surge-cl-gallery-img--pending');
    const done = () => {
      img.classList.remove('surge-cl-gallery-img--pending');
      pending -= 1;
      if (pending <= 0) galleryRoot.classList.remove('surge-cl-gallery--img-fade');
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  });
  if (pending > 0) galleryRoot.classList.add('surge-cl-gallery--img-fade');
}

function surgeNormalizeProductPath(url) {
  if (url == null || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return new URL(trimmed).pathname || '';
    }
  } catch {
    /* ignore */
  }
  return trimmed.split('?')[0].split('#')[0] || '';
}

/**
 * For history.replaceState: prefer the visible product path in the address bar when it is a
 * `/products/...` URL. After a combined-listing shell swap, `data-product-url` on variant UI
 * points at the child product while the bar stays on the parent — using the dataset URL would
 * incorrectly rewrite the location to the child.
 */
function surgeHistoryProductPathPreferBar(fetchProductUrl) {
  const barPath = surgeNormalizeProductPath(
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : ''
  );
  const datasetPath = surgeNormalizeProductPath(
    String(fetchProductUrl == null ? '' : fetchProductUrl).split('?')[0]
  );
  if (barPath && barPath.includes('/products/')) return barPath;
  return datasetPath;
}

/** Parent handle for sessionStorage; keep aligned with `scripts/combined-listings/parent-product-handle.mjs`. */
function surgeParentProductHandleFromUrl(productUrl) {
  const path = surgeNormalizeProductPath(productUrl);
  const parts = path.split('/').filter(Boolean);
  const pi = parts.indexOf('products');
  if (pi >= 0 && parts[pi + 1]) return parts[pi + 1];
  return '';
}

const SURGE_CL_SESSION_PREFIX = 'surge_cl_opts_v1:';

/** Prefer address bar path (parent PDP under §8.2) over `data-product-url` (often the child). */
function surgeCombinedListingSessionStorageFullKey(currentProductUrl) {
  const bar = typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : '';
  let handle = surgeParentProductHandleFromUrl(bar);
  if (!handle) handle = surgeParentProductHandleFromUrl(currentProductUrl);
  return handle ? SURGE_CL_SESSION_PREFIX + handle : '';
}

/**
 * @param {string} currentProductUrl
 * @param {ParentNode | null | undefined} scopeRoot — prefer `#surge-combined-listing-shell` so a second product form on the page does not steal reads.
 */
function surgeSaveCombinedListingNonSiblingSelections(currentProductUrl, scopeRoot) {
  const key = surgeCombinedListingSessionStorageFullKey(currentProductUrl);
  if (!key) return;
  const root =
    scopeRoot && typeof scopeRoot.querySelector === 'function'
      ? scopeRoot
      : typeof document !== 'undefined'
        ? document.getElementById('surge-combined-listing-shell')
        : null;
  const siblingHost =
    (root &&
      root.querySelector(
        'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
      )) ||
    (typeof document !== 'undefined' &&
      document.querySelector(
        'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
      ));
  if (!siblingHost) return;
  const parentSel = siblingHost.dataset.parent;
  if (!parentSel) return;
  const parent =
    (root && root.querySelector(parentSel)) ||
    (typeof document !== 'undefined' ? document.querySelector(parentSel) : null);
  if (!parent) return;
  const skipIdx = parseInt(siblingHost.dataset.optionIndex, 10);
  if (Number.isNaN(skipIdx)) return;

  /** @type {string[]} */
  let base = [];
  try {
    const raw = siblingHost.dataset.currentSet;
    if (raw) base = JSON.parse(raw);
  } catch {
    base = [];
  }
  if (!Array.isArray(base)) base = [];

  /** @type {Record<string, string>} */
  const map = {};

  parent.querySelectorAll('variant-pills, variant-dropdown').forEach((el) => {
    const idx = parseInt(el.dataset.optionIndex, 10);
    if (Number.isNaN(idx) || idx === skipIdx) return;
    const tag = el.tagName.toLowerCase();
    let val = null;
    if (tag === 'variant-pills') {
      const inp = el.querySelector('input[type="radio"]:checked');
      val = inp?.value ?? null;
      if (!val) {
        const sb = el.querySelector('.size-button.active');
        val = sb?.dataset?.size ?? null;
      }
    } else {
      const sel = el.querySelector('.option-value.selected');
      val = sel?.dataset?.value ?? null;
    }
    if (val != null && String(val).trim() !== '') {
      map[String(idx)] = String(val);
    }
  });

  base.forEach((v, i) => {
    if (i === skipIdx) return;
    const k = String(i);
    if (map[k] != null) return;
    if (v != null && String(v).trim() !== '') map[k] = String(v);
  });

  try {
    delete map[String(skipIdx)];
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

/**
 * @param {readonly { available?: boolean, options: string[] }[]} variants
 * @param {readonly string[]} desired
 */
function surgeResolveOptSetForChildVariants(variants, desired) {
  if (!variants?.length || !desired?.length) {
    return { options: desired ? [...desired] : [], variant: variants?.[0] ?? null };
  }
  const len = desired.length;
  const matches = (v) => v.options.length === len && v.options.every((o, i) => o === desired[i]);
  const exactAvail = variants.find((v) => v.available && matches(v));
  if (exactAvail) return { options: [...exactAvail.options], variant: exactAvail };
  const exact = variants.find(matches);
  if (exact) return { options: [...exact.options], variant: exact };
  const firstAvail = variants.find((v) => v.available);
  if (firstAvail) return { options: [...firstAvail.options], variant: firstAvail };
  const first = variants[0];
  return { options: first ? [...first.options] : [...desired], variant: first ?? null };
}

/**
 * Combined listings: keep the **parent** PDP path in the address bar while the rendered
 * shell reflects the selected child (Shopify `option_values` on parent URL).
 */
function surgeReplaceStateParentCombinedListing(parentProductUrl, { optionValueId, variantId } = {}) {
  const path = surgeHistoryProductPathPreferBar(parentProductUrl);
  if (!path || optionValueId == null || String(optionValueId).trim() === '') return;
  let qs;
  try {
    qs = new URLSearchParams(window.location.search);
  } catch {
    qs = new URLSearchParams();
  }
  qs.set('option_values', String(optionValueId));
  if (variantId != null && variantId !== '') {
    qs.set('variant', String(variantId));
  }
  const s = qs.toString();
  window.history.replaceState({}, '', s ? `${path}?${s}` : path);
}

/**
 * After a combined-listing child shell merge, keep the **parent** product path in the address
 * bar (with `option_values` / `variant` from `surgeReplaceStateParentCombinedListing`) while
 * leaving the visible product title to the merged **child** markup. Updates `document.title`
 * from the shell heading and preserves a trailing store/suffix segment when possible.
 *
 * @param {HTMLElement | null | undefined} shell
 */
function surgeSyncDocumentTitleFromShellProductHeading(shell) {
  if (!shell || typeof document === 'undefined') return;
  const h1 = shell.querySelector('h1.custom-product-title');
  const h2 = shell.querySelector('.heading .title h2');
  const titleText = (h1?.textContent || h2?.textContent || '').trim();
  if (!titleText) return;
  const cur = document.title.trim();
  const m = cur.match(/\s[–—|]\s/);
  if (m && m.index != null) {
    const suffix = cur.slice(m.index + m[0].length).trim();
    document.title = suffix ? `${titleText}${m[0]}${suffix}` : titleText;
    return;
  }
  document.title = titleText;
}

function surgeRestoreParentCatalogChrome(shell) {
  if (!shell?.dataset) return;
  const rootPathRaw = shell.dataset.surgeClRootPath;
  const wantPath = surgeNormalizeProductPath(rootPathRaw);
  const barPath = surgeNormalizeProductPath(
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : ''
  );
  if (wantPath && barPath && wantPath !== barPath) {
    let qs;
    try {
      qs = new URLSearchParams(window.location.search);
    } catch {
      qs = new URLSearchParams();
    }
    const s = qs.toString();
    window.history.replaceState({}, '', s ? `${wantPath}?${s}` : wantPath);
  }
  surgeSyncDocumentTitleFromShellProductHeading(shell);
}

/**
 * @param {HTMLElement} host — variant-pills or variant-dropdown
 * @param {string} selectedValue — option label / value string
 * @returns {{ siblingProductUrl: string, optionValueId: string } | null}
 */
function surgeFindCombinedListingSiblingMeta(host, selectedValue) {
  if (host.dataset.combinedSiblingOption !== 'true' || selectedValue == null) return null;
  const tag = host.tagName.toLowerCase();
  if (tag === 'variant-pills') {
    const inputs = [...host.querySelectorAll('input[type="radio"]')];
    const input =
      inputs.find((i) => i.value === selectedValue && i.checked) ||
      inputs.find((i) => i.value === selectedValue);
    const label = input?.nextElementSibling;
    if (!label || label.tagName !== 'LABEL') return null;
    const u = label.getAttribute('data-sibling-product-url');
    const id = label.getAttribute('data-option-value-id');
    if (u && id) return { siblingProductUrl: u, optionValueId: id };
    return null;
  }
  if (tag === 'variant-dropdown') {
    const opts = [...host.querySelectorAll('.option-value')];
    const opt =
      opts.find((o) => o.dataset.value === selectedValue && o.classList.contains('selected')) ||
      opts.find((o) => o.dataset.value === selectedValue);
    if (!opt) return null;
    const u = opt.getAttribute('data-sibling-product-url');
    const id = opt.getAttribute('data-option-value-id');
    if (u && id) return { siblingProductUrl: u, optionValueId: id };
    return null;
  }
  return null;
}

function surgeCombinedListingOptionIdsMatch(domAttr, urlParam) {
  const a = String(domAttr ?? '').trim();
  const b = String(urlParam ?? '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const tail = (s) => {
    const m = String(s).match(/(\d+)\s*$/);
    return m ? m[1] : String(s);
  };
  return tail(a) === tail(b);
}

/**
 * Resolve sibling URL + option value id from `data-option-value-id` (URL param may use GID or numeric tail).
 */
function surgeCombinedListingDomMetaForOptionValueId(host, optionValueId) {
  if (!host || optionValueId == null || String(optionValueId).trim() === '') return null;
  const tag = host.tagName.toLowerCase();
  if (tag === 'variant-pills') {
    for (const inp of host.querySelectorAll('input[type="radio"]')) {
      const label = inp.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') continue;
      const id = label.getAttribute('data-option-value-id');
      if (!id || !surgeCombinedListingOptionIdsMatch(id, optionValueId)) continue;
      const u = label.getAttribute('data-sibling-product-url');
      if (u && id) return { siblingProductUrl: u, optionValueId: id };
    }
  } else if (tag === 'variant-dropdown') {
    for (const o of host.querySelectorAll('.option-value')) {
      const id = o.getAttribute('data-option-value-id');
      if (!id || !surgeCombinedListingOptionIdsMatch(id, optionValueId)) continue;
      const u = o.getAttribute('data-sibling-product-url');
      if (u && id) return { siblingProductUrl: u, optionValueId: id };
    }
  }
  return null;
}

function surgeCombinedListingSelectedSiblingValue(host) {
  const tag = host.tagName.toLowerCase();
  if (tag === 'variant-pills') {
    const inp = host.querySelector('input[type="radio"]:checked');
    if (inp?.value) return inp.value;
    const first = host.querySelector('input[type="radio"]');
    return first?.value ?? null;
  }
  if (tag === 'variant-dropdown') {
    const sel = host.querySelector('.option-value.selected');
    if (sel?.dataset?.value) return sel.dataset.value;
    const first = host.querySelector('.option-value');
    return first?.dataset?.value ?? null;
  }
  return null;
}

function surgeResolveCombinedListingShellMetaForInitialLoad(host) {
  let param = null;
  try {
    param = new URLSearchParams(window.location.search).get('option_values');
  } catch {
    param = null;
  }
  const hasOptionValuesParam = !!(param && String(param).trim());

  const barPath = surgeNormalizeProductPath(
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : ''
  );
  const productPath = surgeNormalizeProductPath(host?.dataset?.productUrl);

  if (hasOptionValuesParam) {
    const parts = String(param)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    for (const id of parts) {
      const m = surgeCombinedListingDomMetaForOptionValueId(host, id);
      if (m) return m;
    }
    return null;
  }

  // Parent catalog URL (`data-product-url` matches bar) with no `?option_values=` must not
  // hydrate from the first combined-listing pill — that option is almost always a *child* product
  // (e.g. Yellow), so we would replace the shell on every parent visit (wrong).
  if (productPath && barPath && productPath === barPath) {
    return null;
  }

  const val = surgeCombinedListingSelectedSiblingValue(host);
  if (!val) return null;
  return surgeFindCombinedListingSiblingMeta(host, val);
}

/**
 * Parent combined-listing URLs render with the **parent** product in Liquid (`product.media` is often a single
 * shared image). After the buyer picks a background we fetch the **child** section into `#surge-combined-listing-shell`.
 * Run that same fetch once on load so the gallery matches the selected `option_values` / default sibling.
 */
async function surgeMaybeSwapCombinedListingShellOnLoad() {
  const host = document.querySelector(
    'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
  );
  if (!host?.dataset?.productUrl || !host.dataset.sectionId) {
    return false;
  }
  const meta = surgeResolveCombinedListingShellMetaForInitialLoad(host);
  if (!meta?.siblingProductUrl || !meta.optionValueId) {
    return false;
  }
  const barPath = surgeNormalizeProductPath(
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : ''
  );
  const siblingPath = surgeNormalizeProductPath(meta.siblingProductUrl);
  if (siblingPath && barPath && siblingPath === barPath) {
    return false;
  }
  return surgeSwapCombinedListingShell({
    siblingProductUrl: meta.siblingProductUrl,
    optionValueId: meta.optionValueId,
    sectionId: host.dataset.sectionId,
    loaderSelector: host.dataset.loader || null,
    currentProductUrl: host.dataset.productUrl,
    swapTrigger: 'initial-load'
  });
}

function surgeRehydrateAfterCombinedListingSwap(shell) {
  const jv =
    document.getElementById('json_prod_var') ||
    shell?.querySelector('#json_prod_var');
  if (jv?.textContent) {
    try {
      window.productVariants = JSON.parse(jv.textContent.trim());
    } catch {
      /* ignore */
    }
  }
  window.surgeReinitProductGalleryLightboxDeferred?.(shell);
  surgeReinitImageUploadersAfterCombinedListingSwap();
}

/**
 * The single-image-uploader draws its idle drop zone from an inline <script>
 * (snippets/single-image-uploader-js.liquid) that runs once on first render.
 * A combined-listing shell swap replaces the upload slot via innerHTML, which does
 * NOT execute inline <script>s, so the fresh `.photo-upload-slot-1` is left empty and
 * the uploader looks unrendered. Re-run the already-global initializer (defined by the
 * parent render's inline script) so the idle drop zone is redrawn against the new slot.
 * No-ops safely when the swapped-in child has no uploader (getSingleSlot() returns null).
 */
function surgeReinitImageUploadersAfterCombinedListingSwap() {
  try {
    if (typeof window.initSingleImageUploader === 'function') {
      window.initSingleImageUploader();
    }
  } catch {
    /* ignore uploader re-init errors — never block the shell swap */
  }
}

/**
 * @param {object | null | undefined} product
 * @returns {string[]}
 */
function surgeProductTagsList(product) {
  const t = product?.tags;
  if (Array.isArray(t)) return t.map((x) => String(x));
  if (typeof t === 'string') {
    return t
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * v1 structural guard with a human reason when the Ajax JSON overlay is skipped.
 *
 * @param {object | null | undefined} product Storefront `/products/*.js` object
 * @param {HTMLElement | null | undefined} nextShell Parsed `#surge-combined-listing-shell` from section HTML
 * @returns {{ ok: boolean, detail: string }}
 */
function surgeCombinedListingStorefrontJsonGuardResult(product, nextShell) {
  if (!product) {
    return {
      ok: false,
      detail: 'No Ajax product object (fetch failed, aborted, or non-JSON response). Variant scripts stay from section HTML only.'
    };
  }
  if (!nextShell) {
    return { ok: false, detail: 'No parsed #surge-combined-listing-shell in section response.' };
  }
  for (const tag of surgeProductTagsList(product)) {
    const t = tag.trim().toLowerCase();
    if (t === 'custom-design' || t === 'custom-charm-design') {
      return {
        ok: false,
        detail: `Blocked product tag "${tag}" — overlay skipped; section HTML scripts only.`
      };
    }
  }
  const opts = product.options;
  if (!Array.isArray(opts) || opts.length === 0) {
    return { ok: false, detail: 'Ajax product has no options array — overlay skipped.' };
  }
  const pickers = nextShell.querySelectorAll('variant-pills, variant-dropdown');
  if (pickers.length !== opts.length) {
    return {
      ok: false,
      detail: `Picker vs options mismatch: ${pickers.length} variant-pills/dropdown in section shell vs ${opts.length} Ajax product.options — overlay skipped.`
    };
  }
  const variants = product.variants;
  if (!Array.isArray(variants) || variants.length === 0) {
    return { ok: false, detail: 'Ajax product has no variants — overlay skipped.' };
  }
  return { ok: true, detail: '' };
}

/**
 * @param {object | null | undefined} product
 * @param {HTMLElement | null | undefined} nextShell
 * @returns {boolean}
 */
function surgeCombinedListingStorefrontJsonGuardsPass(product, nextShell) {
  return surgeCombinedListingStorefrontJsonGuardResult(product, nextShell).ok;
}

/**
 * @param {object} v Variant from storefront JSON
 * @param {number} optionCount
 * @returns {string[]}
 */
function surgeVariantOptionsFromStorefront(v, optionCount) {
  // Storefront `.js` variants carry the full option-value array (all dimensions), while
  // `option1..3` cap at 3. Prefer `v.options` so combined-listing 4th+ options (e.g. Number
  // of Pets) survive into the client JSON used for variant matching.
  if (Array.isArray(v.options) && v.options.length) {
    const full = v.options.map((o) => (o == null ? '' : String(o)));
    return optionCount > 0 ? full.slice(0, optionCount) : full;
  }
  const parts = [v.option1, v.option2, v.option3];
  /** @type {string[]} */
  const out = [];
  for (let i = 0; i < optionCount && i < 3; i++) {
    const p = parts[i];
    out.push(p == null ? '' : String(p));
  }
  return out;
}

/**
 * @param {object} v
 * @returns {number | null}
 */
function surgeFeaturedImageIdFromStorefrontVariant(v) {
  if (v.featured_media && v.featured_media.id != null) return v.featured_media.id;
  const fi = v.featured_image;
  if (fi != null && typeof fi === 'object' && fi.id != null) return fi.id;
  if (typeof fi === 'number' && !Number.isNaN(fi)) return fi;
  if (typeof fi === 'string' && fi !== '' && !Number.isNaN(Number(fi))) return Number(fi);
  return null;
}

/**
 * Slim shape matching `sections/product-section.liquid` `product_json_id` script (for `data-json-selector`).
 *
 * @param {object} product
 * @returns {{ handle: string, available: boolean, variants: object[] }}
 */
function surgeBuildThemeProductJsonFromStorefront(product) {
  const optionCount = Array.isArray(product.options) ? product.options.length : 0;
  const variants = (product.variants || []).map((v) => ({
    id: v.id,
    price: v.price,
    compare_at_price: v.compare_at_price ?? null,
    featured_image: surgeFeaturedImageIdFromStorefrontVariant(v),
    featured_image_index: null,
    available: !!v.available,
    options: surgeVariantOptionsFromStorefront(v, optionCount),
    option1: v.option1 ?? null,
    option2: v.option2 ?? null,
    option3: v.option3 ?? null,
    quantity_rule: v.quantity_rule ?? null
  }));
  return {
    handle: product.handle != null ? String(product.handle) : '',
    available: !!product.available,
    variants
  };
}

/**
 * Full variants for `#json_prod_var` — ensure each row has `options[]` for theme JS that mirrors Liquid.
 *
 * @param {object} product
 * @returns {object[]}
 */
function surgeEnrichVariantsForJsonProdVar(product) {
  const optionCount = Array.isArray(product.options) ? product.options.length : 0;
  return (product.variants || []).map((v) => {
    const copy = { ...v };
    if (!Array.isArray(copy.options) || copy.options.length === 0) {
      copy.options = surgeVariantOptionsFromStorefront(v, optionCount);
    }
    return copy;
  });
}

/**
 * Overwrite section-merged JSON with storefront-canonical data when guards pass.
 *
 * @param {object} product
 * @param {HTMLElement} liveShell
 * @returns {boolean} Whether both script targets were updated
 */
function surgeApplyStorefrontProductJsonToMount(product, liveShell) {
  if (!product || !liveShell) return false;
  const mount =
    liveShell.closest('[id^="shopify-section-"]') || liveShell.parentElement || document.body;
  const slim = surgeBuildThemeProductJsonFromStorefront(product);
  const pj = mount.querySelector('script[type="application/json"][id^="product-json-"]');
  const jv =
    document.getElementById('json_prod_var') ||
    mount.querySelector('#json_prod_var');
  if (!pj || !jv) return false;
  try {
    pj.textContent = JSON.stringify(slim);
    jv.textContent = JSON.stringify(surgeEnrichVariantsForJsonProdVar(product));
    return true;
  } catch {
    return false;
  }
}

/**
 * Section fetch returns `#product-json-*` / `#json_prod_var` outside `#surge-combined-listing-shell` in Liquid.
 * After a shell DOM update, `data-json-selector` can point at new ids while stale scripts remain — restore never runs.
 */
function surgeSyncCombinedListingSectionJsonScripts(sectionDoc, liveShell) {
  if (!sectionDoc || !liveShell) return;
  const mount =
    liveShell.closest('[id^="shopify-section-"]') ||
    liveShell.parentElement ||
    document.body;

  const srcPj = sectionDoc.querySelector('script[type="application/json"][id^="product-json-"]');
  if (srcPj?.id) {
    mount
      .querySelectorAll('script[type="application/json"][id^="product-json-"]')
      .forEach((n) => n.remove());
    mount.appendChild(srcPj.cloneNode(true));
  }

  const srcJv = sectionDoc.getElementById('json_prod_var');
  if (srcJv) {
    const destJv = document.getElementById('json_prod_var');
    if (destJv) {
      destJv.textContent = srcJv.textContent;
    } else {
      mount.appendChild(srcJv.cloneNode(true));
    }
  }

  const srcLd = sectionDoc.querySelector('script[type="application/ld+json"]');
  const destLd = mount.querySelector('script[type="application/ld+json"]');
  if (srcLd && destLd) {
    destLd.textContent = srcLd.textContent;
  }
}

/* Combined-listing siblings render from the same Liquid, so live and fetched markup differ only in
   values. Patching just those (instead of innerHTML / Splide re-mount) keeps unchanged nodes on
   screen — an unchanged `src` is never rewritten, so shared media never repaints. */

/** `data-full-src` carries the deferred full-resolution URL (assets/product-gallery-defer.js). */
const SURGE_PATCH_IMG_ATTRS = ['src', 'srcset', 'sizes', 'alt', 'width', 'height', 'loading', 'fetchpriority', 'class', 'data-full-src'];
/** Non-image media re-mounts on its own; never patched in place. */
const SURGE_PATCH_MEDIA_BAIL_SEL = 'video, iframe, model-viewer, .media--media_tag';

/**
 * Queue attribute writes for `names` that differ; identical values are skipped.
 *
 * @param {Element} live
 * @param {Element} next
 * @param {string[]} names
 * @param {Array<() => void>} ops
 * @returns {boolean} true when at least one write was queued
 */
function surgePlanAttrOps(live, next, names, ops) {
  let changed = false;
  for (const name of names) {
    const nv = next.getAttribute(name);
    if (live.getAttribute(name) === nv) continue;
    changed = true;
    ops.push(() => {
      if (nv === null) live.removeAttribute(name);
      else live.setAttribute(name, nv);
    });
  }
  return changed;
}

/** `data-*` only — `id`, `class` and inline styles on a Splide host are runtime-owned. */
function surgePlanDataAttrOps(live, next, ops) {
  const names = new Set();
  for (const a of next.attributes) if (a.name.startsWith('data-')) names.add(a.name);
  for (const a of live.attributes) if (a.name.startsWith('data-')) names.add(a.name);
  return surgePlanAttrOps(live, next, [...names], ops);
}

/**
 * Plan one media container (`.splide__slide` or stacked `[data-gal-item]`). Touches Liquid-authored
 * bits only; Splide-owned classes (`is-active`) and inline styles are left so the slider keeps state.
 *
 * @returns {null | { dimensionsChanged: boolean }} null = structure differs, caller must fall back
 */
function surgePlanMediaItemPatch(liveItem, nextItem, ops) {
  if (liveItem.querySelector(SURGE_PATCH_MEDIA_BAIL_SEL) || nextItem.querySelector(SURGE_PATCH_MEDIA_BAIL_SEL)) {
    return null;
  }
  const liveImgs = [...liveItem.querySelectorAll('img')];
  const nextImgs = [...nextItem.querySelectorAll('img')];
  if (liveImgs.length !== nextImgs.length) return null;
  const liveWraps = [...liveItem.querySelectorAll('.gallery-main-ratio-wrap')];
  const nextWraps = [...nextItem.querySelectorAll('.gallery-main-ratio-wrap')];
  if (liveWraps.length !== nextWraps.length) return null;

  surgePlanAttrOps(liveItem, nextItem, ['data-media-id', 'data-title'], ops);
  for (let i = 0; i < liveWraps.length; i++) {
    surgePlanAttrOps(liveWraps[i], nextWraps[i], ['class', 'style'], ops);
  }
  let dimensionsChanged = false;
  for (let i = 0; i < liveImgs.length; i++) {
    if (
      liveImgs[i].getAttribute('width') !== nextImgs[i].getAttribute('width') ||
      liveImgs[i].getAttribute('height') !== nextImgs[i].getAttribute('height')
    ) {
      dimensionsChanged = true;
    }
    surgePlanAttrOps(liveImgs[i], nextImgs[i], SURGE_PATCH_IMG_ATTRS, ops);
  }
  return { dimensionsChanged };
}

/**
 * Diff `.gallery-column` against fetched markup; plan only when every slider, slide and stacked
 * item lines up one-to-one. Eligibility: `scripts/combined-listings/gallery-in-place-patch-eligibility.test.mjs`.
 *
 * @returns {null | { ops: Array<() => void>, needsRefresh: boolean }}
 */
function surgePlanGalleryColumnPatch(gLive, gNext) {
  const liveHosts = [...gLive.querySelectorAll('splide-slider')];
  const nextHosts = [...gNext.querySelectorAll('splide-slider')];
  if (!liveHosts.length || liveHosts.length !== nextHosts.length) return null;

  /** @type {Array<() => void>} */
  const ops = [];
  let needsRefresh = false;

  for (let i = 0; i < liveHosts.length; i++) {
    const lid = liveHosts[i].id || '';
    if (!lid || lid !== (nextHosts[i].id || '')) return null;
    const liveSlides = [...liveHosts[i].querySelectorAll('.splide__slide:not(.splide__slide--clone)')];
    const nextSlides = [...nextHosts[i].querySelectorAll('.splide__slide')];
    if (!liveSlides.length || liveSlides.length !== nextSlides.length) return null;
    for (let s = 0; s < liveSlides.length; s++) {
      const plan = surgePlanMediaItemPatch(liveSlides[s], nextSlides[s], ops);
      if (!plan) return null;
      if (plan.dimensionsChanged) needsRefresh = true;
    }
    surgePlanDataAttrOps(liveHosts[i], nextHosts[i], ops);
  }

  const liveStacked = gLive.querySelector('.gallery-stacked-desktop-only');
  const nextStacked = gNext.querySelector('.gallery-stacked-desktop-only');
  if (!!liveStacked !== !!nextStacked) return null;
  if (liveStacked && nextStacked) {
    // `setGallery` reorders the stacked list in place (`--swapped`) — index pairing would then map
    // the wrong media onto the wrong slot.
    if (liveStacked.querySelector('[data-gal-list].--swapped')) return null;
    const liveItems = [...liveStacked.querySelectorAll('[data-gal-item]')];
    const nextItems = [...nextStacked.querySelectorAll('[data-gal-item]')];
    if (liveItems.length !== nextItems.length) return null;
    for (let i = 0; i < liveItems.length; i++) {
      surgePlanAttrOps(liveItems[i], nextItems[i], ['class'], ops);
      const plan = surgePlanMediaItemPatch(liveItems[i], nextItems[i], ops);
      if (!plan) return null;
      if (plan.dimensionsChanged) needsRefresh = true;
    }
  }

  return { ops, needsRefresh };
}

/**
 * Update `.gallery-column` from fetched markup: in-place attribute patch first (no re-mount), then
 * the Splide re-mount that keeps each `<splide-slider>` host node (no custom-element disconnect),
 * then `gLive.innerHTML = gNext.innerHTML` when structure does not match.
 * Re-mount eligibility (count + id match): see `scripts/combined-listings/gallery-splide-preserve-eligibility.test.mjs`.
 *
 * @param {HTMLElement} gLive
 * @param {HTMLElement} gNext
 * @returns {'gallery-in-place-patch' | 'splide-preserve' | 'gallery-full-innerHTML'}
 */
function surgeApplyGalleryColumnPreserveSplide(gLive, gNext) {
  const patch = surgePlanGalleryColumnPatch(gLive, gNext);
  if (patch) {
    patch.ops.forEach((op) => op());
    // The patch resets already-upgraded frames to their placeholder; re-promote in the same pass.
    if (typeof window !== 'undefined') window.surgeUpgradeGalleryVisibleSlides?.(gLive);
    // Slider stays mounted; re-measure only when intrinsic dimensions changed (autoHeight).
    if (patch.needsRefresh) {
      [...gLive.querySelectorAll('splide-slider')].forEach((h) => surgeSplideGalleryRefresh(h));
    }
    if (typeof window !== 'undefined') {
      window.surgeReinitProductGalleryLightboxDeferred?.(gLive.closest('#surge-combined-listing-shell') || gLive);
    }
    return 'gallery-in-place-patch';
  }

  const liveHosts = [...gLive.querySelectorAll('splide-slider')];
  const nextHosts = [...gNext.querySelectorAll('splide-slider')];
  if (liveHosts.length !== nextHosts.length || liveHosts.length === 0) {
    gLive.innerHTML = gNext.innerHTML;
    return 'gallery-full-innerHTML';
  }
  for (let i = 0; i < liveHosts.length; i++) {
    const lid = liveHosts[i].id || '';
    const nid = nextHosts[i].id || '';
    if (!lid || lid !== nid) {
      gLive.innerHTML = gNext.innerHTML;
      return 'gallery-full-innerHTML';
    }
  }

  const liveStacked = gLive.querySelector('.gallery-stacked-desktop-only');
  const nextStacked = gNext.querySelector('.gallery-stacked-desktop-only');
  if (!!liveStacked !== !!nextStacked) {
    gLive.innerHTML = gNext.innerHTML;
    return 'gallery-full-innerHTML';
  }
  if (liveStacked && nextStacked) {
    liveStacked.innerHTML = nextStacked.innerHTML;
  }

  for (let i = 0; i < liveHosts.length; i++) {
    const liveHost = liveHosts[i];
    const nextHost = nextHosts[i];
    const lp = liveHost.parentElement;
    const np = nextHost.parentElement;
    if (lp && np && lp.className !== np.className) {
      lp.className = np.className;
    }
    if (typeof liveHost.reinitializeFrom === 'function') {
      liveHost.reinitializeFrom(nextHost);
    } else {
      gLive.innerHTML = gNext.innerHTML;
      return 'gallery-full-innerHTML';
    }
  }

  liveHosts.forEach((h) => surgeSplideGalleryRefresh(h));
  if (typeof window !== 'undefined') {
    window.surgeReinitProductGalleryLightboxDeferred?.(gLive.closest('#surge-combined-listing-shell') || gLive);
  }
  return 'splide-preserve';
}

/**
 * @typedef {object} SurgeClShellDomMeta
 * @property {'column-partial' | 'full-shell-innerHTML'} shellPath
 * @property {'gallery-in-place-patch' | 'splide-preserve' | 'gallery-full-innerHTML' | 'no-gallery-columns'} galleryPath
 * @property {'surge-cl-buybox-swap-morph' | 'surge-cl-buybox-morph' | 'surge-cl-buybox-swap-innerHTML' | 'surge-cl-buybox-innerHTML' | 'content-column-innerHTML' | 'n/a'} buyboxPath
 */

/** Subtrees with their own mounted runtime state — replaced whole, never morphed into. */
const SURGE_MORPH_OPAQUE_SEL = 'splide-slider, .splide, [data-splide], video, iframe, model-viewer, canvas';

/** Copy attributes that differ; drop the ones the incoming markup no longer has. */
function surgeMorphAttributes(live, next) {
  for (const a of [...next.attributes]) {
    if (live.getAttribute(a.name) !== a.value) live.setAttribute(a.name, a.value);
  }
  for (const a of [...live.attributes]) {
    if (!next.hasAttribute(a.name)) live.removeAttribute(a.name);
  }
}

/**
 * Morph `live` into `next` in place; unchanged text/attributes are not written, so untouched
 * controls keep their pixels, listeners and JS state.
 *
 * @returns {boolean} false when the nodes are not compatible and the caller must replace
 */
function surgeMorphElement(live, next) {
  if (live.tagName !== next.tagName) return false;
  if (live.matches?.(SURGE_MORPH_OPAQUE_SEL) || next.matches?.(SURGE_MORPH_OPAQUE_SEL)) {
    if (live.outerHTML !== next.outerHTML) live.replaceWith(next.cloneNode(true));
    return true;
  }
  surgeMorphAttributes(live, next);
  // The `checked` property survives an attribute patch, so re-align it. Text inputs are left alone
  // on purpose: that keeps a typed pet name instead of wiping it on every sibling change.
  if (live instanceof HTMLInputElement && (live.type === 'radio' || live.type === 'checkbox')) {
    const nextChecked = next.hasAttribute('checked');
    if (live.checked !== nextChecked) live.checked = nextChecked;
  }
  surgeMorphChildNodes(live, next);
  return true;
}

function surgeMorphChildNodes(liveParent, nextParent) {
  const liveKids = [...liveParent.childNodes];
  const nextKids = [...nextParent.childNodes];
  const shared = Math.min(liveKids.length, nextKids.length);

  for (let i = 0; i < shared; i++) {
    const l = liveKids[i];
    const n = nextKids[i];
    if (l.nodeType !== n.nodeType) {
      l.replaceWith(n.cloneNode(true));
      continue;
    }
    if (l.nodeType === Node.TEXT_NODE || l.nodeType === Node.COMMENT_NODE) {
      if (l.data !== n.data) l.data = n.data;
      continue;
    }
    if (l.nodeType !== Node.ELEMENT_NODE) continue;
    if (!surgeMorphElement(l, n)) l.replaceWith(n.cloneNode(true));
  }
  for (let i = liveKids.length - 1; i >= shared; i--) liveKids[i].remove();
  for (let i = shared; i < nextKids.length; i++) liveParent.appendChild(nextKids[i].cloneNode(true));
}

/**
 * Swap buy-column markup for a combined-listing child. When Liquid wraps blocks in
 * `#surge-cl-buybox-swap`, only that subtree is touched so the outer `#surge-cl-buybox`
 * stays stable and debug UI can sit outside the swap zone.
 *
 * Morph first — only prices, labels and option values differ, so the column is never blanked.
 * `innerHTML` behind `visibility: hidden` (revealed after the post-swap restore) is the fallback.
 *
 * Applied synchronously either way. We do not use `document.startViewTransition` here: its update
 * callback can run after a `requestAnimationFrame`, so `surgePostCombinedListingShellOptionRestore`
 * would run on pre-swap DOM and sessionStorage restores (e.g. size) would be lost.
 *
 * @param {HTMLElement} buyLive
 * @param {HTMLElement} buyNext
 * @returns {'surge-cl-buybox-swap-morph' | 'surge-cl-buybox-morph' | 'surge-cl-buybox-swap-innerHTML' | 'surge-cl-buybox-innerHTML'}
 */
function surgeApplyBuyboxInnerFromNext(buyLive, buyNext) {
  const swapLive = buyLive.querySelector('#surge-cl-buybox-swap');
  const swapNext = buyNext.querySelector('#surge-cl-buybox-swap');
  const useSwapZone = !!(swapLive && swapNext);
  const live = useSwapZone ? swapLive : buyLive;
  const next = useSwapZone ? swapNext : buyNext;

  try {
    surgeMorphChildNodes(live, next);
    return useSwapZone ? 'surge-cl-buybox-swap-morph' : 'surge-cl-buybox-morph';
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[combined-listings] buy box morph failed, falling back to innerHTML', err);
    }
  }

  live.style.visibility = 'hidden';
  live.innerHTML = next.innerHTML;
  return useSwapZone ? 'surge-cl-buybox-swap-innerHTML' : 'surge-cl-buybox-innerHTML';
}

/**
 * Update the live combined-listing shell from parsed section markup.
 * Prefer swapping only `.gallery-column` and `.content-column` contents so the shell node
 * and grid wrappers stay stable (less layout thrash than replacing the entire subtree).
 * Falls back to full `innerHTML` when the fetched tree does not match that layout.
 *
 * @returns {SurgeClShellDomMeta | null}
 */
function surgeApplyCombinedListingShellDom(nextShell, liveShell) {
  if (!nextShell || !liveShell) return null;

  const gLive = liveShell.querySelector(':scope > .gallery-column');
  const gNext = nextShell.querySelector(':scope > .gallery-column');
  const cLive = liveShell.querySelector(':scope > .content-column');
  const cNext = nextShell.querySelector(':scope > .content-column');

  if (cLive && cNext) {
    const bothGallery = gLive && gNext;
    const neitherGallery = !gLive && !gNext;
    if (bothGallery || neitherGallery) {
      /** @type {'splide-preserve' | 'gallery-full-innerHTML' | 'no-gallery-columns'} */
      let galleryPath = 'no-gallery-columns';
      if (bothGallery) {
        galleryPath = surgeApplyGalleryColumnPreserveSplide(gLive, gNext);
      }
      const buyLive = cLive.querySelector('#surge-cl-buybox');
      const buyNext = cNext.querySelector('#surge-cl-buybox');
      /** @type {'surge-cl-buybox-swap-innerHTML' | 'surge-cl-buybox-innerHTML' | 'content-column-innerHTML'} */
      let buyboxPath = 'content-column-innerHTML';
      if (buyLive && buyNext) {
        buyboxPath = surgeApplyBuyboxInnerFromNext(buyLive, buyNext);
      } else {
        cLive.innerHTML = cNext.innerHTML;
      }
      return { shellPath: 'column-partial', galleryPath, buyboxPath };
    }
  }

  liveShell.innerHTML = nextShell.innerHTML;
  return {
    shellPath: 'full-shell-innerHTML',
    galleryPath: 'no-gallery-columns',
    buyboxPath: 'n/a'
  };
}

/**
 * @param {string[]} lines
 */
function surgeClDebugLiveSetLines(lines) {
  const text = lines.join('\n');
  document.querySelectorAll('.surge-cl-debug-live').forEach((el) => {
    el.textContent = text;
  });
}

function surgeRestorePageScrollY(scrollY) {
  if (typeof scrollY !== 'number' || Number.isNaN(scrollY)) return;
  const restore = () => {
    if (Math.abs(window.scrollY - scrollY) > 2) window.scrollTo(0, scrollY);
  };
  restore();
  requestAnimationFrame(restore);
  requestAnimationFrame(() => requestAnimationFrame(restore));
  setTimeout(restore, 0);
  setTimeout(restore, 80);
  setTimeout(restore, 280);
}

/**
 * Fetch Section Rendering HTML for sibling product; merge into #surge-combined-listing-shell
 * (column-level when possible).
 */
async function surgeSwapCombinedListingShell({
  siblingProductUrl,
  optionValueId,
  sectionId,
  loaderSelector,
  currentProductUrl,
  swapTrigger = 'sibling-change'
}) {
  const targetPath = surgeNormalizeProductPath(siblingProductUrl);
  const currentPath = surgeNormalizeProductPath(currentProductUrl);
  if (!targetPath || targetPath === currentPath) return false;

  const curShell = document.getElementById('surge-combined-listing-shell');
  if (!curShell) return false;

  surgeSaveCombinedListingNonSiblingSelections(currentProductUrl, curShell);

  if (surgeCombinedListingAbort) surgeCombinedListingAbort.abort();
  surgeCombinedListingAbort = new AbortController();
  const mySwapGen = ++surgeClShellSwapGen;

  const scrollLockY = window.scrollY;
  surgeSuppressGallerySyncForCombinedListing(700);
  curShell.classList.add('surge-cl-shell--busy');

  const clearShellBusy = () => {
    curShell.classList.remove('surge-cl-shell--busy');
    curShell.style.minHeight = '';
    curShell.querySelector('#surge-cl-buybox-swap')?.style.removeProperty('visibility');
    const buy = curShell.querySelector('#surge-cl-buybox');
    if (buy?.style.visibility === 'hidden') buy.style.removeProperty('visibility');
  };

  /** Avoid clearing busy/visibility when a newer swap superseded this one (shared AbortSignal). */
  const clearShellBusyIfStillOwner = () => {
    if (mySwapGen !== surgeClShellSwapGen) return;
    clearShellBusy();
  };

  try {
    const signal = surgeCombinedListingAbort.signal;
    const [{ html }, storefrontProduct] = await Promise.all([
      surgeGetCombinedListingSectionHtml(targetPath, sectionId, optionValueId, signal),
      surgeGetStorefrontProductJson(targetPath, signal)
    ]);
    if (signal.aborted) {
      clearShellBusyIfStillOwner();
      return false;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextShell = doc.getElementById('surge-combined-listing-shell');
    if (!nextShell) {
      console.warn('[combined-listings] #surge-combined-listing-shell missing in response or live DOM');
      surgeClDebugLiveSetLines([
        '=== Combined-listing swap failed ===',
        `Child path: ${targetPath}`,
        'Reason: section HTML had no #surge-combined-listing-shell after parse.'
      ]);
      clearShellBusyIfStillOwner();
      return false;
    }

    /** @type {SurgeClShellDomMeta | null} */
    let domMeta = null;
    /** @type {'ajax-json-overlay' | 'section-json-only'} */
    let variantJsonPath = 'section-json-only';
    /** @type {string} */
    let variantJsonNote = '';

    const applyDom = () => {
      domMeta = surgeApplyCombinedListingShellDom(nextShell, curShell);
      surgeSyncCombinedListingSectionJsonScripts(doc, curShell);

      const guard = surgeCombinedListingStorefrontJsonGuardResult(storefrontProduct, nextShell);
      if (storefrontProduct && guard.ok) {
        const ok = surgeApplyStorefrontProductJsonToMount(storefrontProduct, curShell);
        if (ok) {
          variantJsonPath = 'ajax-json-overlay';
          variantJsonNote =
            'Overlay ON: #product-json-* and #json_prod_var were replaced using the Ajax product JSON for this child.';
        } else {
          variantJsonNote =
            'Overlay SKIPPED after guard passed: script nodes missing or JSON write failed; section scripts were re-synced from the fetched section.';
          surgeSyncCombinedListingSectionJsonScripts(doc, curShell);
        }
      } else {
        variantJsonNote = guard.detail;
      }

      surgeRehydrateAfterCombinedListingSwap(curShell);
    };

    // Direct replacement, never a dissolve: the old shell stays visible while incoming images
    // decode off-DOM, so no frame of page background shows through. Skipped on initial load — the
    // gallery is already hidden by `surge-cl-initial-mask` and there is no previous sibling to hold.
    const holdOldUntilReady = swapTrigger !== 'initial-load';
    let decodedBeforeCommit = true;

    if (holdOldUntilReady) {
      decodedBeforeCommit = await surgeDecodeIncomingGalleryImages(nextShell);
      if (signal.aborted || mySwapGen !== surgeClShellSwapGen) {
        clearShellBusyIfStillOwner();
        return false;
      }
    }

    // Intentionally no `document.startViewTransition`: root/shell VT snapshots read as
    // "section removed and reinserted" while `.content-column` is fully replaced.
    applyDom();

    // Patch path keeps the mounted slider, so the active index survives; snap to slide 1 in this
    // commit — leaving it to the rAF2 reset would read as two image changes.
    if (domMeta?.galleryPath === 'gallery-in-place-patch') {
      surgeResetSplideGalleriesToFirst(curShell);
    }

    const dm = domMeta || {
      shellPath: 'column-partial',
      galleryPath: 'no-gallery-columns',
      buyboxPath: 'n/a'
    };
    const shellHuman =
      dm.shellPath === 'column-partial'
        ? 'Column merge (shell node kept; gallery + content column patched).'
        : 'Full shell replace (entire #surge-combined-listing-shell innerHTML — layout fallback).';
    const galleryHuman =
      dm.galleryPath === 'gallery-in-place-patch'
        ? 'Gallery in-place patch (no Splide re-mount; only changed img attrs / media ids written).'
        : dm.galleryPath === 'splide-preserve'
          ? 'Splide preserve (same splide-slider hosts; reinitializeFrom + refresh — structure differed).'
          : dm.galleryPath === 'gallery-full-innerHTML'
            ? 'Gallery fallback (full .gallery-column innerHTML).'
            : 'No gallery columns in this shell merge.';
    const buyHuman =
      dm.buyboxPath === 'surge-cl-buybox-swap-morph'
        ? 'Buy box: #surge-cl-buybox-swap morphed in place (changed text/attrs only; nothing hidden).'
        : dm.buyboxPath === 'surge-cl-buybox-morph'
          ? 'Buy box: #surge-cl-buybox morphed in place (no inner swap wrapper in section HTML).'
          : dm.buyboxPath === 'surge-cl-buybox-swap-innerHTML'
            ? 'Buy box: #surge-cl-buybox-swap innerHTML (morph fallback; hidden until restore).'
            : dm.buyboxPath === 'surge-cl-buybox-innerHTML'
              ? 'Buy box: #surge-cl-buybox innerHTML only (morph fallback; no inner swap wrapper).'
              : dm.buyboxPath === 'content-column-innerHTML'
                ? 'Buy box: whole .content-column innerHTML (no #surge-cl-buybox pair).'
                : 'Buy box: n/a (full shell path).';
    const ajaxUrl = `${targetPath}.js`;
    const jsonHuman =
      variantJsonPath === 'ajax-json-overlay'
        ? `VARIANT SCRIPTS: AJAX JSON OVERLAY — GET ${ajaxUrl} → wrote #product-json-* + #json_prod_var (after section merge).`
        : storefrontProduct
          ? `VARIANT SCRIPTS: SECTION JSON ONLY — GET ${ajaxUrl} returned data but overlay was skipped; scripts = section HTML only.`
          : `VARIANT SCRIPTS: SECTION JSON ONLY — GET ${ajaxUrl} did not yield usable product JSON; scripts = section HTML only.`;
    const triggerHuman =
      swapTrigger === 'initial-load'
        ? 'Trigger: initial page load (default combined-listing child for this parent URL).'
        : 'Trigger: shopper changed background / combined-listing sibling.';

    const debugLines = [
      `=== Last combined-listing swap @ ${new Date().toLocaleTimeString(undefined, { hour12: false })} ===`,
      triggerHuman,
      `Child path: ${targetPath}`,
      '',
      jsonHuman,
      variantJsonNote,
      '',
      'UI / DOM (always uses section HTML for visible gallery + buy box in v1):',
      `  ${shellHuman}`,
      `  ${galleryHuman}`,
      `  ${buyHuman}`,
      '',
      `Timing: images decoded off-DOM before commit (${decodedBeforeCommit ? 'in budget — direct swap, no fade' : `over ${SURGE_CL_SWAP_DECODE_BUDGET_MS}ms — cold-cache fade fallback`}); shell opacity never animated. rAF1 = restore + replaceState + chrome + variant:change + scroll; rAF2 = clear shell busy (buy box reveal only on the innerHTML fallback).`
    ];
    const flushDebug = () => surgeClDebugLiveSetLines(debugLines);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(flushDebug, { timeout: 600 });
    } else {
      setTimeout(flushDebug, 0);
    }

    const galleryCol = curShell.querySelector(':scope > .gallery-column');
    // Cold-cache fallback only: when the incoming images did not decode inside the hold budget,
    // fade in whatever is still loading. On the normal (pre-decoded) path this is a no-op, so a
    // ready gallery is never dimmed after it has already painted.
    const runGalleryFade = () => {
      if (mySwapGen !== surgeClShellSwapGen) return;
      if (dm.galleryPath === 'gallery-in-place-patch') return;
      if (decodedBeforeCommit && dm.galleryPath === 'splide-preserve') return;
      surgeFadeGalleryImagesIn(galleryCol);
    };

    // Defer option restore + variant events + scroll to the next frame so the browser can
    // paint new buy-box markup before we touch radios, data-current-set, and Splide slides.
    // Same-frame innerHTML + setGallery was visibly harsh on combined-listing controls.
    // Keep #surge-cl-buybox-swap visibility:hidden through restore, replaceState, catalog chrome,
    // and variant:change so the first painted frame is not an intermediate control state (then
    // rAF2 clearShellBusy reveals + drops busy together).
    requestAnimationFrame(() => {
      if (signal.aborted) {
        clearShellBusyIfStillOwner();
        return;
      }
      if (mySwapGen !== surgeClShellSwapGen) {
        return;
      }

      try {
        surgeSuppressGallerySyncForCombinedListing(500);
        surgePostCombinedListingShellOptionRestore(curShell, currentProductUrl, {
          optionValueId,
          swapTrigger
        });
      } catch (restoreErr) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[combined-listings] post-swap option restore failed', restoreErr);
        }
      }

      const variantInput =
        curShell.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        curShell.querySelector('form input[name="id"]');
      const vid = variantInput?.value;
      surgeReplaceStateParentCombinedListing(currentProductUrl, {
        optionValueId,
        variantId: vid || undefined
      });
      surgeRestoreParentCatalogChrome(curShell);

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variantId: vid ? parseInt(vid, 10) : null,
            skipGallerySync: true
          }
        })
      );
      document.dispatchEvent(new CustomEvent('surge:combined-listing-shell-replaced', { bubbles: true }));

      surgeScheduleCombinedListingSiblingPrefetch();

      window.scrollTo(0, scrollLockY);
      surgeRestorePageScrollY(scrollLockY);
      // Next frame: reveal buy box + drop busy after variant:change listeners have run, so the
      // shopper does not see one paint of "restored" UI and a second paint from `variant:change`.
      requestAnimationFrame(() => {
        if (signal.aborted) {
          clearShellBusyIfStillOwner();
          return;
        }
        if (mySwapGen !== surgeClShellSwapGen) {
          return;
        }
        clearShellBusyIfStillOwner();
        surgeResetSplideGalleriesToFirst(curShell);
        surgeRestorePageScrollY(scrollLockY);
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(runGalleryFade, { timeout: 900 });
        } else {
          setTimeout(runGalleryFade, 120);
        }
      });
    });

    return true;
  } catch (e) {
    clearShellBusyIfStillOwner();
    if (e.name !== 'AbortError') {
      console.error(e);
      surgeClDebugLiveSetLines([
        '=== Combined-listing swap error ===',
        String(e && e.message ? e.message : e),
        `Child path: ${surgeNormalizeProductPath(siblingProductUrl) || '(unknown)'}`
      ]);
    }
    return false;
  }
}

const getJsonParse = (attrStr) => {
  let arr = [];
  const strArr = attrStr ? JSON.parse(attrStr) : null;
  if (!strArr) return arr = null;
  strArr.forEach(selector => arr.push(document.querySelector(selector)));
  return arr;
}

const sectionArrRender = (secArr, response) => {
  secArr.forEach(section => {
    const html = new DOMParser().parseFromString(response.sections[section.id], 'text/html').getElementById(section.id);
    if (!html) return; // Skip if element not found
    section.selectors.forEach(selector => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) {
        targetElement.outerHTML = sourceElement.outerHTML;
      }
    });
  });
}

/**
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {HTMLImageElement | null}
 */
function surgeGetActiveGalleryImage(scopeRoot) {
  const root =
    scopeRoot ||
    document.querySelector('.main-product-section') ||
    document.getElementById('surge-combined-listing-shell');
  if (!root) return null;

  const activeSlideImg = root.querySelector(
    '.product-gallery-main .splide__slide.is-active img, .product-gallery-main .splide__slide.is-visible img'
  );
  if (activeSlideImg?.src) return activeSlideImg;

  const stackedImg = root.querySelector(
    '.gallery-stacked-images .gallery-variant-preview-img, .gallery-stacked-images .stacked-image-item img'
  );
  if (stackedImg?.src) return stackedImg;

  const fallbackImg = root.querySelector('.product-gallery-main img[src], .gallery-main-ratio-wrap img[src]');
  return fallbackImg?.src ? fallbackImg : null;
}

// Helper function to refresh and open cart drawer
const refreshAndOpenCartDrawer = (shouldBumpBadge = false) => {
  const badgeOptions = shouldBumpBadge ? { bump: true } : undefined;

  const applyCartRefresh = (cart) => {
    if (typeof updateCartIconDirectly === 'function') {
      updateCartIconDirectly(cart.item_count || 0, badgeOptions);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  };

  const openDrawerIfNeeded = () => {
    const cartSlide = document.querySelector('cart-slide');
    if (!cartSlide) return;
    cartSlide.refreshCart?.();
    if (cartSlide.toggleCart && !cartSlide.classList.contains('active')) {
      cartSlide.toggleCart();
    }
  };

  if (typeof refreshCartJS === 'function') {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        applyCartRefresh(cart);
        openDrawerIfNeeded();
      })
      .catch(() => {});
    return;
  }

  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => {
      applyCartRefresh(cart);
      openDrawerIfNeeded();
    })
    .catch(() => {});
};

const fetchAtc = (sections, config, element, loaderSelector) => {
  this.error = true;
  const btn = element?.querySelector?.('button[name="add"]');
  const loader = btn?.querySelector?.('.submit_add_cart_loader');
  if (loader) loader.classList.add('active');
  if (btn) {
    btn.disabled = true;
    btn.classList.add('loading');
  }
  togglerLoader(true, loaderSelector);
  atcError(null, element, false);
  fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((response) => {
      if (!response.sections) return atcError(response, element, true);
      this.error = false;
      sectionArrRender(sections, response);

      const productSection = element.closest('.main-product-section');
      const galleryImg = surgeGetActiveGalleryImage(productSection);

      if (typeof updateCartIconDirectly === 'function' && element?.form) {
        const badge = document.getElementById('cartCount');
        const currentCount = badge ? parseInt(badge.textContent, 10) || 0 : 0;
        const addedQty = getAtcMainQty(element.form);
        updateCartIconDirectly(currentCount + addedQty, { bump: true });
      }

      let drawerDelay = 0;
      if (typeof window.flyToCart === 'function' && galleryImg) {
        drawerDelay = window.flyToCart(galleryImg);
      }

      setTimeout(() => refreshAndOpenCartDrawer(false), drawerDelay);
      if (element && typeof element.showNotification === 'function') {
        const addedMessage =
          element._surgeLastAddedMessage ||
          surgeBuildAddedToCartMessage(element.form, element.closest('.main-product-section'));
        requestAnimationFrame(() => {
          element.showNotification(addedMessage, 'success');
        });
      }
    })
    .catch((e) => {
      console.log(e);
      throw e;
    })
    .finally(() => {
      const _btn = element?.querySelector?.('button[name="add"]');
      const _loader = _btn?.querySelector?.('.submit_add_cart_loader');
      if (_loader) _loader.classList.remove('active');
      // Always re-enable the button after request completes
      if (_btn) {
        _btn.classList.remove('loading');
        _btn.disabled = false;
        _btn.removeAttribute('disabled');
      }
      resetQty(element.dataset.form);
      togglerLoader(false, loaderSelector);
      if (!this.error) {
        if (element.dataset.redirect) return window.location.href = element.dataset.redirect;
        if (element.dataset.modal === 'true') {
          const drawerToggler = document.querySelector(element.dataset.modalRef);
          if (drawerToggler) drawerToggler.dispatchEvent(new Event('click'));
        }
        if (element.recipientForm) element.recipientForm.resetRecipientForm();
      }
    }
    );
}

const resetQty = (elem) => {
  if (!elem) return;
  const form = document.querySelector(elem);
  if (!form) return;
  const qtyField = form.quantity;
  if (!qtyField || qtyField.min == null || qtyField.min === '') return;
  qtyField.value = parseFloat(qtyField.min);
  qtyField.dispatchEvent(new Event('input'));
}

/**
 * @param {object | null | undefined} variant
 * @returns {number | string | null}
 */
function surgeGetVariantFeaturedMediaId(variant) {
  if (!variant) return null;
  if (variant.featured_media && variant.featured_media.id != null) {
    return variant.featured_media.id;
  }
  const fi = variant.featured_image;
  if (fi != null && typeof fi === 'object' && fi.id != null) return fi.id;
  if (typeof fi === 'number' && !Number.isNaN(fi)) return fi;
  if (typeof fi === 'string' && fi.trim() !== '' && !Number.isNaN(Number(fi))) return Number(fi);
  if (typeof fi === 'string' && fi.trim() !== '') return fi.trim();
  return null;
}

/**
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {HTMLElement | null}
 */
function surgeGetVariantGalleryHost(scopeRoot) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return null;
  return root.querySelector(
    'variant-pills[data-json-selector][data-galleries], variant-dropdown[data-json-selector][data-galleries]'
  );
}

/**
 * @param {readonly { options?: string[], available?: boolean }[]} variants
 * @param {readonly string[]} optSet
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {object | null}
 */
function surgeFindVariantForGallery(variants, optSet, scopeRoot) {
  if (!variants?.length || !optSet?.length) return null;

  const strict = variants.find(
    (v) =>
      Array.isArray(v.options) &&
      v.options.length === optSet.length &&
      v.options.every((o, i) => String(o) === String(optSet[i]))
  );
  if (strict) return strict;

  const styleSelect = scopeRoot?.querySelector('.native-style-select');
  if (styleSelect?.classList.contains('placeholder-active')) return null;
  if (optSet.some((o) => o == null || String(o).trim() === '')) return null;

  return surgeResolveOptSetForChildVariants(variants, optSet).variant ?? null;
}

/**
 * Splide.go(index) for slide with matching data-media-id; retries until mounted.
 *
 * @param {HTMLElement | null | undefined} galRoot
 * @param {number | string} imageId
 * @param {number} [attempt]
 * @returns {boolean}
 */
function surgeGoSplideToMediaId(galRoot, imageId, attempt = 0) {
  const maxAttempts = 20;
  if (!galRoot || imageId == null || String(imageId).trim() === '') return false;

  const host = galRoot.closest?.('splide-slider') || galRoot;
  const splide = host.slider ?? galRoot.slider;

  if (!splide?.Components?.Slides) {
    if (attempt < maxAttempts) {
      requestAnimationFrame(() => surgeGoSplideToMediaId(galRoot, imageId, attempt + 1));
    }
    return false;
  }

  const idStr = String(imageId);
  const matches = splide.Components.Slides.filter(`[data-media-id="${idStr}"]`);
  if (!matches?.length || matches[0] == null) return false;

  const targetIdx = matches[0].index;
  if (splide.index !== targetIdx) {
    splide.go(targetIdx);
    surgeSplideGalleryRefresh(host);
  }
  if (typeof window.surgeUpdateProductGalleryArrows === 'function') {
    window.surgeUpdateProductGalleryArrows(host);
  }
  return true;
}

/**
 * Combined-listing color swap: show first gallery slide (not variant featured media).
 *
 * @param {ParentNode | null | undefined} scopeRoot
 * @param {number} [attempt]
 * @returns {boolean}
 */
function surgeResetSplideGalleriesToFirst(scopeRoot, attempt = 0) {
  const maxAttempts = 24;
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return false;

  const hosts = root.querySelectorAll('splide-slider');
  if (!hosts.length) {
    if (attempt < maxAttempts) {
      requestAnimationFrame(() => surgeResetSplideGalleriesToFirst(scopeRoot, attempt + 1));
    }
    return false;
  }

  hosts.forEach((host) => {
    const main = host.slider;
    if (main?.go && typeof main.index === 'number' && main.index !== 0) {
      main.go(0);
    }
    const thumbs = host.syncSlider;
    if (thumbs?.go && typeof thumbs.index === 'number' && thumbs.index !== 0) {
      thumbs.go(0);
    }
    surgeSplideGalleryRefresh(host);
  });
  return true;
}

/**
 * Legacy custom-options pattern: match slide data-title / alt text (see handleSliderImage).
 * Requires every one of `labelValues` to appear in the slide title, so a change to one
 * custom option (e.g. Art Style) can't jump to a slide belonging to a different, still-
 * selected option (e.g. Color) just because the title happens to also contain the new value.
 *
 * @param {readonly string[]} labelValues
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {boolean}
 */
function surgeFocusSplideGalleryByMediaTitles(labelValues, scopeRoot) {
  const tokens = (labelValues || [])
    .map((v) => String(v ?? '').replace(/\s+/g, ''))
    .filter((v) => v !== '');
  if (!tokens.length) return false;

  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return false;

  const host = surgeGetVariantGalleryHost(root);
  if (!host?.dataset.galleries) return false;

  let gallerySelectors;
  try {
    gallerySelectors = JSON.parse(host.dataset.galleries);
  } catch {
    return false;
  }
  if (!Array.isArray(gallerySelectors)) return false;

  for (const selector of gallerySelectors) {
    const gal =
      root.querySelector(selector) ||
      (typeof document !== 'undefined' ? document.querySelector(selector) : null);
    if (!gal) continue;

    const slides = gal.querySelectorAll?.('.splide__slide[data-media-id]') || [];
    for (const slide of slides) {
      const title = (
        slide.getAttribute('data-title') ||
        slide.querySelector('img')?.getAttribute('alt') ||
        ''
      ).replace(/\s+/g, '');
      if (!title || !tokens.every((t) => title.includes(t))) continue;
      const mediaId = slide.getAttribute('data-media-id');
      if (mediaId && surgeGoSplideToMediaId(gal, mediaId)) return true;
    }
  }
  return false;
}

/**
 * Single-value convenience wrapper around surgeFocusSplideGalleryByMediaTitles.
 *
 * @param {string} labelValue
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {boolean}
 */
function surgeFocusSplideGalleryByMediaTitle(labelValue, scopeRoot) {
  return surgeFocusSplideGalleryByMediaTitles([labelValue], scopeRoot);
}

/**
 * Normalized (whitespace-stripped) `data-title`/alt text of every slide across a product's
 * galleries. Used to sanity-check that a candidate custom-option label is actually something
 * that shows up in gallery image titles (e.g. a color/style name), as opposed to unrelated
 * line-item properties like a delivery-date estimate or engraving text.
 *
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {string[]}
 */
function surgeGetGallerySlideTitles(scopeRoot) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return [];

  const host = surgeGetVariantGalleryHost(root);
  if (!host?.dataset.galleries) return [];

  let gallerySelectors;
  try {
    gallerySelectors = JSON.parse(host.dataset.galleries);
  } catch {
    return [];
  }
  if (!Array.isArray(gallerySelectors)) return [];

  const titles = [];
  for (const selector of gallerySelectors) {
    const gal = root.querySelector(selector) || (typeof document !== 'undefined' ? document.querySelector(selector) : null);
    if (!gal?.querySelectorAll) continue;
    gal.querySelectorAll('.splide__slide[data-media-id]').forEach((slide) => {
      const title = (
        slide.getAttribute('data-title') ||
        slide.querySelector('img')?.getAttribute('alt') ||
        ''
      ).replace(/\s+/g, '');
      if (title) titles.push(title);
    });
  }
  return titles;
}

/**
 * Reads the live values of every custom-option line-item-property hidden input
 * (`name="properties[...]"`, excluding its paired "_cors" companion) so a gallery image
 * match can account for every currently-selected custom option at once, not just the one
 * that just changed. These properties (e.g. a swatch-based Background/color picker) are
 * frequently not real Shopify variant options at all — they don't appear in `optSet` and
 * have no per-variant featured image — so this is the only place their value is visible.
 * By the time any handler calls into gallery-sync, the input for the option that just
 * changed has already been committed to its new value (see changeBorderColor/
 * selectBackground/getSelectedValue in snippets/custom-options-js.liquid), so no exclusion
 * of the "current" option is needed here.
 *
 * Not every line-item property is gallery-relevant (e.g. a delivery-date estimate or
 * engraving text never appears in any slide title), and one irrelevant label would poison
 * an "every label must match" search — so candidates are kept only if they actually appear
 * in at least one gallery slide title somewhere on the page.
 *
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {string[]}
 */
function surgeGetActiveLineItemPropertyLabels(scopeRoot) {
  const scope = scopeRoot || document;
  const inputs = scope.querySelectorAll('input[type="hidden"][name^="properties["]');
  const raw = [];

  inputs.forEach((input) => {
    const name = input.getAttribute('name') || '';
    if (name.endsWith('_cors]')) return;
    const val = input.value ? String(input.value).trim() : '';
    if (val) raw.push(val);
  });
  if (!raw.length) return [];

  const titles = surgeGetGallerySlideTitles(scope);
  if (!titles.length) return raw;

  return raw.filter((label) => {
    const normalized = label.replace(/\s+/g, '');
    return titles.some((t) => t.includes(normalized));
  });
}

/**
 * Sync Splide main gallery for active variant pickers (Background child + Style + other options).
 *
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {boolean}
 */
function surgeSyncGalleryFromVariantPickers(scopeRoot) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (surgeIsGallerySyncSuppressed()) {
    surgeResetSplideGalleriesToFirst(root);
    return false;
  }
  const host = surgeGetVariantGalleryHost(root);
  if (!host?.dataset.jsonSelector || !host.dataset.galleries) return false;

  let optSet;
  try {
    optSet = JSON.parse(host.dataset.currentSet);
  } catch {
    return false;
  }
  if (!Array.isArray(optSet) || optSet.length === 0) return false;

  const json = document.querySelector(host.dataset.jsonSelector);
  if (!json?.textContent) return false;

  let data;
  try {
    data = JSON.parse(json.textContent);
  } catch {
    return false;
  }

  const variant = surgeFindVariantForGallery(data.variants, optSet, root);
  if (!variant) return false;

  const mediaId = surgeGetVariantFeaturedMediaId(variant);
  if (mediaId != null) {
    setGallery(mediaId, host.dataset.galleries, false, null);
    return true;
  }

  const styleLabel = surgeGetCurrentStyleOptionLabel(root, host, optSet);
  const customLabels = surgeGetActiveLineItemPropertyLabels(root);

  // Prefer a slide matching style + every active custom option (e.g. Background) together.
  if (styleLabel && customLabels.length) {
    if (surgeFocusSplideGalleryByMediaTitles([styleLabel, ...customLabels], root)) return true;
  }
  // No combo slide for this exact style — keep the currently-selected custom option correct
  // rather than falling through to a style-only match that can land on the wrong color.
  if (customLabels.length && surgeFocusSplideGalleryByMediaTitles(customLabels, root)) return true;
  if (styleLabel) return surgeFocusSplideGalleryByMediaTitle(styleLabel, root);

  return false;
}

/**
 * Reads the currently-selected value of the "style" native variant option — the same option
 * `.native-style-select`'s data-option-index (or the gallery host's own data-option-index)
 * identifies — from a live `optSet`/`data-current-set`. Shared by both gallery-sync directions
 * so a custom-option (e.g. Background) change can also keep whatever Art Style is selected,
 * since Art Style is a real Shopify variant option and therefore invisible to
 * surgeGetActiveLineItemPropertyLabels.
 *
 * @param {ParentNode | null | undefined} scopeRoot
 * @param {Element | null} [host] - pass the already-resolved gallery host to avoid re-querying it
 * @param {readonly string[]} [optSet] - pass the already-parsed current option set to avoid re-parsing it
 * @returns {string}
 */
function surgeGetCurrentStyleOptionLabel(scopeRoot, host, optSet) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return '';

  const galleryHost = host || surgeGetVariantGalleryHost(root);
  if (!galleryHost?.dataset.currentSet) return '';

  let set = optSet;
  if (!set) {
    try {
      set = JSON.parse(galleryHost.dataset.currentSet);
    } catch {
      return '';
    }
  }
  if (!Array.isArray(set)) return '';

  const styleIdx = parseInt(
    galleryHost.closest('.product-card-variants')?.querySelector('.native-style-select')?.dataset.optionIndex ??
      galleryHost.dataset.optionIndex,
    10
  );
  return !Number.isNaN(styleIdx) && set[styleIdx] ? String(set[styleIdx]) : '';
}

/**
 * Bridge for snippets/custom-options-js.liquid handleSliderImage.
 * Custom options (e.g. border color) are metafield-driven line-item properties, not a real
 * variant axis, so this always matches by gallery slide title/alt text rather than resolving
 * a variant — resolving via the variant picker would match on an unrelated native option
 * (e.g. Size) and mask the correct image.
 *
 * Matches against every currently-selected custom option, not just the one that just changed
 * — e.g. changing Background while an Art Style is already selected must keep that Art Style
 * (a real Shopify variant option, invisible to surgeGetActiveLineItemPropertyLabels), rather
 * than jumping to the first slide whose title merely contains the new Background value.
 *
 * @param {string} value
 * @param {string} [_key]
 * @param {boolean} [_showRelatedImages]
 * @returns {boolean}
 */
function surgeSyncGalleryForCustomOption(value, _key, _showRelatedImages) {
  const root =
    document.getElementById('surge-combined-listing-shell') || document.querySelector('.main-product-section');
  const labels = surgeGetActiveLineItemPropertyLabels(root);
  const styleLabel = surgeGetCurrentStyleOptionLabel(root);

  // Prefer a slide matching every active custom option + the currently-selected style together.
  if (styleLabel && labels.length && surgeFocusSplideGalleryByMediaTitles([styleLabel, ...labels], root)) {
    return true;
  }
  // No combo slide for this exact combination — keep the currently-selected style correct
  // rather than falling through to a custom-option-only match that can land on the wrong style.
  if (styleLabel && surgeFocusSplideGalleryByMediaTitle(styleLabel, root)) {
    return true;
  }
  if (labels.length > 1 && surgeFocusSplideGalleryByMediaTitles(labels, root)) {
    return true;
  }
  return surgeFocusSplideGalleryByMediaTitle(value, root);
}

/**
 * @param {ParentNode | Document | null | undefined} scopeRoot
 * @returns {{ el: HTMLScriptElement; data: { variants?: Array<{ id: number; price?: number; compare_at_price?: number | null }> } } | null}
 */
function surgeGetProductVariantJson(scopeRoot) {
  const shell =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!shell || typeof shell.querySelector !== 'function') return null;

  const host = shell.querySelector('[data-json-selector]');
  const sel = host?.getAttribute?.('data-json-selector');
  if (sel) {
    const el = document.querySelector(sel);
    if (el) {
      try {
        return { el, data: JSON.parse(el.textContent) };
      } catch {
        /* fall through */
      }
    }
  }

  const scripts = shell.querySelectorAll('script[type="application/json"][id]');
  for (const el of scripts) {
    try {
      const data = JSON.parse(el.textContent);
      if (data?.variants?.length) return { el, data };
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * Update pajama PDP price row from variant JSON (no full price HTML fetch).
 * @param {number | string | null | undefined} variantId
 * @param {ParentNode | null | undefined} [scopeRoot]
 */
/**
 * Remove legacy price markup when the pajama semantic price row is active.
 * @param {ParentNode | null | undefined} scopeRoot
 */
function surgeCleanupPajamaLegacyPriceMarkup(scopeRoot) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root) return;
  const row = root.querySelector('[data-pdp-price-row]');
  if (!row) return;
  const block = row.closest('.price-block');
  if (!block) return;
  block.querySelectorAll(':scope > .priced, :scope > .cprice, :scope > .price').forEach((el) => {
    el.remove();
  });
}

function surgeSyncPdpPriceRow(variantId, scopeRoot) {
  const root =
    scopeRoot ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section');
  if (!root || variantId == null) return;
  const row = root.querySelector('[data-pdp-price-row]');
  if (!row) return;
  surgeCleanupPajamaLegacyPriceMarkup(root);

  const pkg = surgeGetProductVariantJson(root);
  const variant = pkg?.data?.variants?.find((v) => Number(v.id) === Number(variantId));
  if (!variant || variant.price == null) return;

  const price = Number(variant.price);
  const compareAt =
    variant.compare_at_price != null && variant.compare_at_price !== ''
      ? Number(variant.compare_at_price)
      : 0;
  const onSale = compareAt > price;
  const fmt = window.money_format || window.theme?.moneyFormat || '{{amount}}';

  const currentEl = row.querySelector('[data-pdp-price-current]');
  const compareEl = row.querySelector('[data-pdp-price-compare]');
  const badgeEl = row.querySelector('[data-pdp-price-badge]');

  if (currentEl && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    currentEl.textContent = Shopify.formatMoney(price, fmt);
  }
  if (compareEl && typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    if (onSale) {
      compareEl.textContent = Shopify.formatMoney(compareAt, fmt);
      compareEl.removeAttribute('hidden');
    } else {
      compareEl.setAttribute('hidden', '');
    }
  }
  if (badgeEl) {
    if (onSale && compareAt > 0) {
      const pct = Math.round(((compareAt - price) * 100) / compareAt);
      badgeEl.textContent = `MEMORIAL DAY ${pct}% OFF`;
      badgeEl.removeAttribute('hidden');
    } else {
      badgeEl.setAttribute('hidden', '');
    }
  }
}

/** `variant:change` → pajama price row sync. */
function surgeInitPdpPriceRowSync() {
  if (typeof window !== 'undefined' && window.__surgePdpPriceRowBound) return;
  if (typeof window !== 'undefined') window.__surgePdpPriceRowBound = true;

  document.addEventListener('variant:change', (e) => {
    const id = e.detail?.variantId;
    if (id != null) surgeSyncPdpPriceRow(id);
  });

  document.addEventListener('surge:combined-listing-shell-replaced', () => {
    const shell = document.getElementById('surge-combined-listing-shell');
    const syncFromShell = () => {
      if (!shell) return;
      surgeCleanupPajamaLegacyPriceMarkup(shell);
      const host = shell.querySelector('[data-json-selector]');
      let optSet = host?.dataset?.currentSet;
      if (!optSet) return;
      try {
        optSet = JSON.parse(optSet);
      } catch {
        return;
      }
      const pkg = surgeGetProductVariantJson(shell);
      const variant = pkg?.data?.variants
        ? surgeFindVariantForGallery(pkg.data.variants, optSet, shell)
        : null;
      if (variant?.id) surgeSyncPdpPriceRow(variant.id, shell);
    };
    syncFromShell();
    requestAnimationFrame(syncFromShell);
  });
}

/** Delegated listeners: Background (sibling) + Style + variant UI → Splide. */
function surgeInitVariantGallerySyncListeners() {
  if (typeof window !== 'undefined' && window.__surgeVariantGallerySyncBound) return;
  if (typeof window !== 'undefined') window.__surgeVariantGallerySyncBound = true;

  if (typeof window !== 'undefined') {
    window.surgeSyncGalleryFromVariantPickers = surgeSyncGalleryFromVariantPickers;
    window.surgeSyncGalleryForCustomOption = surgeSyncGalleryForCustomOption;
  }

  let syncRaf = 0;
  const scheduleSync = (scopeRoot) => {
    if (syncRaf) cancelAnimationFrame(syncRaf);
    syncRaf = requestAnimationFrame(() => {
      syncRaf = 0;
      if (surgeIsGallerySyncSuppressed()) {
        surgeResetSplideGalleriesToFirst(scopeRoot);
        return;
      }
      surgeSyncGalleryFromVariantPickers(scopeRoot);
    });
  };

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const scope = t.closest('#surge-combined-listing-shell, .main-product-section');
    if (
      t.matches('.native-style-select') ||
      (t.matches('input[type="radio"]') && t.closest('variant-pills[data-json-selector]'))
    ) {
      scheduleSync(scope);
    }
  });

  document.addEventListener(
    'mousedown',
    (e) => {
      const label = e.target.closest?.('label.variant-element_label');
      if (label?.closest('variant-pills[data-combined-sibling-option="true"]')) e.preventDefault();
    },
    true
  );

  document.addEventListener('click', (e) => {
    const opt = e.target.closest?.('.option-value');
    if (opt?.closest('variant-dropdown[data-json-selector]')) {
      scheduleSync(opt.closest('#surge-combined-listing-shell, .main-product-section'));
    }
  });

  document.addEventListener('variant:change', (e) => {
    const shell =
      document.getElementById('surge-combined-listing-shell') ||
      document.querySelector('.main-product-section');
    if (e.detail?.skipGallerySync) {
      surgeResetSplideGalleriesToFirst(shell);
      return;
    }
    scheduleSync(shell);
  });

  document.addEventListener('surge:combined-listing-shell-replaced', () => {
    const shell = document.getElementById('surge-combined-listing-shell');
    surgeSuppressGallerySyncForCombinedListing(350);
    surgeResetSplideGalleriesToFirst(shell);
    requestAnimationFrame(() => surgeResetSplideGalleriesToFirst(shell));
    setTimeout(() => surgeResetSplideGalleriesToFirst(shell), 80);
    setTimeout(() => surgeResetSplideGalleriesToFirst(shell), 280);
  });
}

const onVariantChange = (jsonSelector, optSet, formsSelector, renders, parentSelector, gallerySelectors, sectionId, productUrl, updateUrl, focusSelector, renderMedia, templateSelector, loaderSelector, checkBundle = false) => {
  const json = document.querySelector(jsonSelector);
  if (!json) return;
  const data = JSON.parse(json.textContent);
  const variants = data.variants;
  const scopeRoot =
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector(parentSelector) ||
    document.querySelector('.main-product-section');

  this.currentVariant = surgeFindVariantForGallery(variants, optSet, scopeRoot);

  setStatuses(this.currentVariant, formsSelector, renders, parentSelector, checkBundle);
  document.dispatchEvent(
    new CustomEvent('variant:change', { bubbles: true, detail: { variantId: this.currentVariant?.id } })
  );
  if (!this.currentVariant) return;

  const mediaId = surgeGetVariantFeaturedMediaId(this.currentVariant);
  if (mediaId != null && !surgeIsGallerySyncSuppressed()) {
    setGallery(mediaId, gallerySelectors, renderMedia, templateSelector);
  }
  renderVariant(renders, this.currentVariant.id, parentSelector, productUrl, sectionId, updateUrl, focusSelector, loaderSelector);
}

const setStatuses = (variant, formElems, renderElems, parentElem, checkBundle) => {
  const forms = getJsonParse(formElems);
  forms.forEach(form => {
    if (!form) return;
    const atcBtn = checkBundle ? form.querySelector('[data-bundle-toggler]') : form.querySelector('[name="add"]');
    
    if (!variant) {
      const parentNode = document.querySelector(parentElem);
      const hasUnselectedOption = parentNode && parentNode.querySelector('.placeholder-active') !== null;
      
      if (hasUnselectedOption) {
        toggleAvail(true, renderElems, parentElem);
        if (atcBtn) {
          const textContainer = atcBtn.querySelector('[data-atc-text]');
          if (textContainer && textContainer.dataset.textAvail) {
            textContainer.innerHTML = textContainer.dataset.textAvail;
          }
          atcBtn.removeAttribute('disabled');
          atcBtn.classList.remove('--disabled');
        }
        return;
      }
      return toggleAvail(false, renderElems, parentElem, atcBtn);
    }
    toggleAvail(true, renderElems, parentElem);
    const input = form.querySelector('[name="id"]');
    input.value = variant.id;
    if (!atcBtn) return;
    const textContainer = atcBtn.querySelector('[data-atc-text]');
    if (variant.available) {
      textContainer.innerHTML = textContainer.dataset.textAvail;
      atcBtn.removeAttribute('disabled');
      atcBtn.classList.remove('--disabled');
      return;
    }
    textContainer.innerHTML = textContainer.dataset.textSold;
    atcBtn.setAttribute('disabled', '');
    atcBtn.classList.add('--disabled');
  });
}

const toggleAvail = (flag, selectors, parent, btn) => {
  const arr = JSON.parse(selectors);
  const parentElem = document.querySelector(parent);
  arr.forEach(selector => {
    const element = parentElem.querySelector(selector);
    if (!element) return;
    flag ? element.classList.remove('--elem-opac') : element.classList.add('--elem-opac');
  });
  if (!btn) return;
  const textContainer = btn.querySelector('[data-atc-text]');
  textContainer.innerHTML = textContainer.dataset.textUnavail;
  btn.setAttribute('disabled', '');
}

/**
 * Re-measure synced main + thumbnail Splides after programmatic slide changes.
 * Without this, the gallery can appear "stuck" on a single slide until the user
 * picks a variant (which triggers the same path again).
 *
 * @param {HTMLElement} galRoot — `splide-slider` (or host that owns `.slider`)
 */
function surgeSplideGalleryRefresh(galRoot) {
  const root = galRoot?.closest?.('splide-slider') || galRoot;
  if (!root) return;
  try {
    if (root.slider && typeof root.slider.refresh === 'function') {
      root.slider.refresh();
    }
    if (root.syncSlider && typeof root.syncSlider.refresh === 'function') {
      root.syncSlider.refresh();
    }
    if (typeof window.surgeScrollSplideSwitcherActiveToLeadingEdge === 'function') {
      const sw = root.querySelector('[data-switcher-thumbs]');
      window.surgeScrollSplideSwitcherActiveToLeadingEdge(sw, root.syncSlider);
    }
  } catch {
    /* Splide not fully mounted */
  }
}

if (typeof window !== 'undefined') {
  window.surgeSplideGalleryRefresh = surgeSplideGalleryRefresh;
}

/**
 * Apply the same gallery focus as after a variant change, using the option set
 * already in `data-current-set` from Liquid (fixes initial load + Splide layout).
 */
function surgeSyncInitialVariantGallery() {
  const seen = new Set();
  document
    .querySelectorAll(
      'variant-pills[data-json-selector][data-current-set],variant-dropdown[data-json-selector][data-current-set]'
    )
    .forEach((host) => {
      const key = host.dataset.jsonSelector;
      if (!key || seen.has(key)) return;
      seen.add(key);
      let optSet;
      try {
        optSet = JSON.parse(host.dataset.currentSet);
      } catch {
        return;
      }
      if (!Array.isArray(optSet) || optSet.length === 0) return;
      const json = document.querySelector(key);
      if (!json?.textContent) return;
      let data;
      try {
        data = JSON.parse(json.textContent);
      } catch {
        return;
      }
      const variants = data.variants;
      if (!variants?.length) return;
      const scopeRoot =
        document.getElementById('surge-combined-listing-shell') ||
        host.closest('.main-product-section');
      const variant = surgeFindVariantForGallery(variants, optSet, scopeRoot);
      if (!variant || !host.dataset.galleries) return;
      const mediaId = surgeGetVariantFeaturedMediaId(variant);
      if (mediaId == null) return;
      setGallery(mediaId, host.dataset.galleries, false, null);
    });
}

/**
 * Active main gallery `data-media-id` for the first Splide matched under the shell (if any).
 * Used to skip redundant `setGallery` after combined-listing merge when the slide is already correct.
 *
 * @param {ParentNode | null | undefined} shell
 * @param {string | undefined} galleriesJson
 * @returns {string | null}
 */
function surgeShellPrimarySplideActiveMediaId(shell, galleriesJson) {
  if (!shell || !galleriesJson) return null;
  /** @type {unknown} */
  let parsed;
  try {
    parsed = JSON.parse(galleriesJson);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const primarySel = parsed[0];
  if (typeof primarySel !== 'string' || !primarySel.trim()) return null;
  const gal =
    shell.querySelector(primarySel) ||
    (typeof document !== 'undefined' ? document.querySelector(primarySel) : null);
  const splide = gal?.slider;
  if (!splide?.Components?.Slides) return null;
  const idx = typeof splide.index === 'number' ? splide.index : 0;
  const slide = splide.Components.Slides.getAt(idx);
  const el = slide?.slide;
  if (!el) return null;
  const fromEl = el.getAttribute('data-media-id') || el.dataset?.mediaId;
  if (fromEl != null && String(fromEl).trim() !== '') return String(fromEl);
  const nested = el.querySelector('[data-media-id]');
  const nid = nested?.getAttribute('data-media-id');
  return nid != null && String(nid).trim() !== '' ? String(nid) : null;
}

const setGallery = (imageId, galleries, render, template) => {
  const arr = JSON.parse(galleries);
  if (!arr) return;
  if (render) {
    const templateElem = document.querySelector(template);
    const templateElement = templateElem.content.querySelector(`[data-media-id="${imageId}"]`);
    const imageContainer = document.querySelector(arr[0]);
    var currentOpacity = parseFloat(imageContainer.style.opacity) || 1;
    var newOpacity = (currentOpacity === 1) ? 0 : 1;
    imageContainer.style.transition = 'opacity 0.3s ease';
    imageContainer.style.opacity = newOpacity;
    imageContainer.classList.remove('.imageContainer');
    setTimeout(() => {
      imageContainer.innerHTML = templateElement.outerHTML;
      setTimeout(() => {
        imageContainer.style.opacity = 1;
      }, 0);
    }, 500);
    return
  }
  arr.forEach(gallery => {
    const elements = document.querySelectorAll(gallery);
    elements.forEach(gal => {
      if (!gal) return;
      switch (gal.slider) {
        case undefined:
          let items = Array.from(gal.querySelectorAll('[data-gal-item]'));
          if (!items.length) return;
          const activeIndex = items.findIndex(item => String(item.dataset.mediaId) === String(imageId));
          if (activeIndex === -1 || activeIndex === 0) return;
          items.unshift(items.splice(activeIndex, 1)[0]);
          gal.innerHTML = '';
          gal.classList.add('--swapped');
          gal.append(...items);
          break;
        default:
          surgeGoSplideToMediaId(gal, imageId);
          break;
      }
    });
  });
}

const renderVariant = (elems, id, parent, url, section, updateUrl, focusSelector, loaderSelector) => {
  togglerLoader(true, loaderSelector);
  fetch(`${url}?variant=${id}&section_id=${section}`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const parentElement = document.querySelector(parent);
      const arr = JSON.parse(elems);
      arr.forEach(selector => {
        const element = parentElement.querySelector(selector);
        const next = html.querySelector(selector);
        // Skip the swap when markup is unchanged (e.g. a title identical across variants) so it
        // does not blank + reflow = flicker on every variant change.
        if (element && next && element.innerHTML !== next.innerHTML) element.innerHTML = next.innerHTML;
      });
    })
    .catch((e) => {
      console.error(e);
      throw e;
    })
    .finally(() => {
      if (updateUrl === 'true') {
        const basePath = surgeHistoryProductPathPreferBar(url);
        let qs;
        try {
          qs = new URLSearchParams(window.location.search);
        } catch {
          qs = new URLSearchParams();
        }
        qs.set('variant', String(id));
        const s = qs.toString();
        window.history.replaceState({}, '', s ? `${basePath}?${s}` : `${basePath}?variant=${id}`);
      } else if (updateUrl === 'variant-id') {
        const u = new URL(window.location.href);
        u.searchParams.set('variant-id', id);
        window.history.replaceState({}, '', u.toString());
      }
      if (focusSelector) {
        const focusParent = document.querySelector(focusSelector);
        trapFocus(focusParent.content ? focusParent.content : focusParent);
      }
      const parentEl = document.querySelector(parent);
      surgeSyncPdpPriceRow(id, parentEl);
      surgeCleanupPajamaLegacyPriceMarkup(parentEl);
      togglerLoader(false, loaderSelector);
    }
    );
}

/**
 * Form + gallery only (no `renderVariant`) so parent-path `replaceState` from combined listings is not overwritten.
 */
function surgeApplyVariantDomSync({
  jsonSelector,
  optSet,
  forms,
  renders,
  mainParent,
  galleries,
  checkBundle,
  skipGalleryWhenActiveMediaIdEquals,
  skipGallery
}) {
  const json = document.querySelector(jsonSelector);
  if (!json) return;
  let data;
  try {
    data = JSON.parse(json.textContent);
  } catch {
    return;
  }
  const variants = data.variants;
  const scopeRoot =
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector(mainParent) ||
    document.querySelector('.main-product-section');
  const currentVariant = surgeFindVariantForGallery(variants, optSet, scopeRoot);
  setStatuses(currentVariant, forms, renders, mainParent, checkBundle || false);
  if (currentVariant?.id) surgeSyncPdpPriceRow(currentVariant.id, scopeRoot);
  if (!currentVariant || skipGallery) return;
  const mediaId = surgeGetVariantFeaturedMediaId(currentVariant);
  if (mediaId == null) return;

  if (
    skipGalleryWhenActiveMediaIdEquals != null &&
    String(skipGalleryWhenActiveMediaIdEquals) === String(mediaId)
  ) {
    return;
  }
  setGallery(mediaId, galleries, false, null);
}

/**
 * Resolve the theme option string for the combined-listing dimension from the Shopify
 * option value id (section `option_values` / click `data-option-value-id`).
 *
 * @param {ParentNode | null | undefined} shell
 * @param {string | number | null | undefined} optionValueId
 * @returns {string | null}
 */
function surgeCoerceSiblingOptionStringFromOptionValueId(shell, optionValueId) {
  const id = optionValueId == null ? '' : String(optionValueId).trim();
  if (!id || !shell || typeof shell.querySelector !== 'function') return null;
  if (!/^\d+$/.test(id)) return null;

  const label = shell.querySelector(`label[data-option-value-id="${id}"]`);
  if (label) {
    const forId = label.getAttribute('for');
    const wrap = label.closest('.variant-options-wrap');
    const input =
      forId && typeof document !== 'undefined'
        ? document.getElementById(forId)
        : wrap?.querySelector('input[type="radio"]');
    const v = input?.value;
    if (v != null && String(v).trim() !== '') return String(v);
  }

  const optEl = shell.querySelector(`.option-value[data-option-value-id="${id}"]`);
  const dv = optEl?.dataset?.value;
  if (dv != null && String(dv).trim() !== '') return String(dv);

  return null;
}

/**
 * @param {HTMLElement | null | undefined} shell
 * @param {string} currentProductUrl
 * @param {{ optionValueId?: string | number; swapTrigger?: string } | null | undefined} restoreOpts
 */
function surgePostCombinedListingShellOptionRestore(shell, currentProductUrl, restoreOpts) {
  const optionValueId = restoreOpts?.optionValueId;
  const siblingHost =
    shell?.querySelector(
      'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
    ) ||
    document.querySelector(
      'variant-pills[data-combined-sibling-option="true"], variant-dropdown[data-combined-sibling-option="true"]'
    );
  if (!siblingHost) return;

  const parentSel = siblingHost.dataset.parent;
  const jsonSelector = siblingHost.dataset.jsonSelector;
  if (!parentSel || !jsonSelector) return;
  const parent =
    (shell && shell.querySelector(parentSel)) ||
    (typeof document !== 'undefined' ? document.querySelector(parentSel) : null);
  const jsonEl = typeof document !== 'undefined' ? document.querySelector(jsonSelector) : null;
  if (!parent || !jsonEl) return;

  let data;
  try {
    data = JSON.parse(jsonEl.textContent);
  } catch {
    return;
  }
  const variants = data.variants;
  if (!variants?.length) return;

  const skipIdx = parseInt(siblingHost.dataset.optionIndex, 10);
  const formInput =
    shell?.querySelector('form[action*="/cart/add"] input[name="id"]') ||
    shell?.querySelector('form input[name="id"]');
  const formVid = formInput?.value;
  let baseVariant = variants.find((v) => String(v.id) === String(formVid));
  if (!baseVariant) baseVariant = variants.find((v) => v.available) || variants[0];
  if (!baseVariant?.options?.length) return;

  /** @type {string[]} */
  let tentative = [...baseVariant.options];
  const storageKey = surgeCombinedListingSessionStorageFullKey(currentProductUrl);
  if (storageKey) {
    let raw;
    try {
      raw = sessionStorage.getItem(storageKey);
    } catch {
      raw = null;
    }
    if (raw) {
      try {
        const map = JSON.parse(raw);
        if (map && typeof map === 'object') {
          Object.keys(map).forEach((k) => {
            const i = parseInt(k, 10);
            if (Number.isNaN(i) || i === skipIdx) return;
            const val = map[k];
            if (val != null && val !== '' && i >= 0 && i < tentative.length) tentative[i] = String(val);
          });
        }
      } catch {
        /* ignore corrupt storage */
      }
    }
  }

  const coercedSibling = surgeCoerceSiblingOptionStringFromOptionValueId(shell, optionValueId);
  if (coercedSibling != null && !Number.isNaN(skipIdx) && skipIdx >= 0 && skipIdx < tentative.length) {
    tentative[skipIdx] = coercedSibling;
  }

  const { options: resolved } = surgeResolveOptSetForChildVariants(variants, tentative);
  const jsonStr = JSON.stringify(resolved);
  parent.querySelectorAll('variant-pills, variant-dropdown').forEach((h) => {
    if (h.getAttribute('data-current-set') !== jsonStr) {
      h.setAttribute('data-current-set', jsonStr);
    }
  });

  resolved.forEach((val, idx) => {
    const pill = parent.querySelector(`variant-pills[data-option-index="${idx}"]`);
    if (pill) {
      const inp = [...pill.querySelectorAll('input[type="radio"]')].find((r) => r.value === val);
      if (inp) inp.checked = true;
      const sizeBtn = [...pill.querySelectorAll('.size-button')].find((b) => b.dataset.size === val);
      if (sizeBtn) {
        pill.querySelectorAll('.size-button').forEach((b) => b.classList.remove('active'));
        sizeBtn.classList.add('active');
      }
    }
  });

  const bundle = siblingHost.dataset.bundle === 'true';
  surgeApplyVariantDomSync({
    jsonSelector,
    optSet: resolved,
    forms: siblingHost.dataset.forms,
    renders: siblingHost.dataset.renders,
    mainParent: siblingHost.dataset.mainParent,
    galleries: siblingHost.dataset.galleries,
    checkBundle: bundle,
    skipGallery: true
  });
  surgeResetSplideGalleriesToFirst(shell);
}

const atcError = (response, elem, flag) => {
  const errElem = elem?.dataset?.errorSelector ? document.querySelector(elem.dataset.errorSelector) : null;
  const errSpan = errElem?.querySelector('[data-error]');
  const recepForm = elem?.form?.querySelector?.('recipient-form');

  if (!flag) {
    if (errElem) errElem.classList.add('hidden');
    if (errSpan) errSpan.innerText = '';
    if (recepForm) recepForm.clearErrorMessage();
    // Clear addon errors
    clearAddonErrors();
    return;
  }

  if (recepForm && response?.errors) recepForm.displayErrorMessage(null, response.errors);

  // Check if error is related to sold-out addon
  const errorMessage = response?.description || response?.message || '';
  const isSoldOutError = errorMessage.toLowerCase().includes('sold out') ||
    errorMessage.toLowerCase().includes('out of stock') ||
    errorMessage.toLowerCase().includes('not available');

  if (isSoldOutError && errorMessage.toLowerCase().includes('addon')) {
    // Show error below addons section
    showAddonError(errorMessage);
    scrollToAddons();
  } else if (typeof response?.description !== 'object' && errElem && errSpan) {
    errElem.classList.remove('hidden');
    errSpan.innerText = response?.description || '';
  }

  if (elem?.dataset?.form) resetQty(elem.dataset.form);
}

// Show error message below addons section
const showAddonError = (message) => {
  // Find or create addon error container
  const addonsSection = document.querySelector('.product-addons') ||
    document.querySelector('[data-product-addons]');

  if (!addonsSection) return;

  let errorContainer = addonsSection.querySelector('.product-addons__error');

  if (!errorContainer) {
    errorContainer = document.createElement('div');
    errorContainer.className = 'product-addons__error';
    errorContainer.style.cssText = `
      background: #fee;
      color: #c00;
      padding: 12px 16px;
      margin: 12px 0;
      border-radius: 4px;
      border-left: 4px solid #c00;
      font-size: 14px;
      display: none;
    `;
    addonsSection.appendChild(errorContainer);
  }

  errorContainer.textContent = message;
  errorContainer.style.display = 'block';

  // Auto-hide after 8 seconds
  setTimeout(() => {
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }, 8000);
}

// Clear addon errors
const clearAddonErrors = () => {
  const errorContainers = document.querySelectorAll('.product-addons__error');
  errorContainers.forEach(container => {
    container.style.display = 'none';
  });
}

// Scroll to addons section
const scrollToAddons = () => {
  const addonsSection = document.querySelector('.product-addons') ||
    document.querySelector('[data-product-addons]');

  if (addonsSection) {
    // Smooth scroll to addons section with offset for header
    const headerOffset = 100; // Adjust based on your header height
    const elementPosition = addonsSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
}

class VariantPills extends HTMLElement {
  constructor() {
    super();

    this.variantChange = this.onInputChange.bind(this);
    this.addEventListener('change', this.variantChange);
    this.sizeButtonClick = this.onSizeButtonClick.bind(this);
  }

  connectedCallback() {
    // Add click listeners to size buttons
    this.setupSizeButtons();
  }

  setupSizeButtons() {
    const sizeButtons = this.querySelectorAll('.size-button-list .size-button');
    sizeButtons.forEach(button => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', this.sizeButtonClick);
    });
  }

  onSizeButtonClick(event) {
    const button = event.currentTarget;
    const value = button.dataset.size;
    if (!value) return;

    // Update active state
    const buttonList = button.closest('.size-button-list');
    if (buttonList) {
      buttonList.querySelectorAll('.size-button').forEach(b => b.classList.remove('active'));
      button.classList.add('active');
    }

    // Trigger variant change
    this.setVariantOptions(value);
  }

  onInputChange() {
    const input = this.querySelector('input[type="radio"]:checked');
    if (input) {
      this.setVariantOptions(input.value);
    }
  }

  setVariantOptions(value) {
    const siblingMeta = surgeFindCombinedListingSiblingMeta(this, value);
    if (siblingMeta) {
      const targetPath = surgeNormalizeProductPath(siblingMeta.siblingProductUrl);
      const currentPath = surgeNormalizeProductPath(this.dataset.productUrl);
      if (targetPath && targetPath !== currentPath) {
        void surgeSwapCombinedListingShell({
          siblingProductUrl: siblingMeta.siblingProductUrl,
          optionValueId: siblingMeta.optionValueId,
          sectionId: this.dataset.sectionId,
          loaderSelector: this.dataset.loader || null,
          currentProductUrl: this.dataset.productUrl,
          swapTrigger: 'sibling-change'
        });
        return;
      }
    }

    const parent = document.querySelector(this.dataset.parent);
    const mainParent = document.querySelector(this.dataset.mainParent);
    const errElem = document.querySelector(this.dataset.errorSelector);
    if (!parent) return;
    if (mainParent && mainParent.hasAttribute('data-bundle-included')) {
      const bundleCalculator = document.querySelector('bundle-calculator');
      mainParent.removeAttribute('data-bundle-included');
      bundleCalculator.init(null, null, null, null, parseFloat(mainParent.dataset.bundleVariant));
    }
    if (errElem) errElem.classList.add('hidden');
    const currentSet = JSON.parse(this.dataset.currentSet);
    const index = parseInt(this.dataset.optionIndex);
    currentSet[index] = value;
    // Update all variant-pills and variant-dropdowns in the parent
    const pills = parent.querySelectorAll('variant-pills');
    pills.forEach(pill => pill.setAttribute('data-current-set', JSON.stringify(currentSet)));
    const dropdowns = parent.querySelectorAll('variant-dropdown');
    dropdowns.forEach(dropdown => dropdown.setAttribute('data-current-set', JSON.stringify(currentSet)));
    this.setOptions(pills, currentSet);
    let availableSet = null;
    if (this.dataset.checkSets === 'true') {
      const sets = JSON.parse(this.dataset.availableSets);
      const checkSet = (val) => {
        if (!val || !Array.isArray(sets)) return null;
        return sets.find(set => set.includes(val));
      };
      availableSet = checkSet(value);
    }
    onVariantChange(this.dataset.jsonSelector, availableSet ? availableSet : currentSet, this.dataset.forms, this.dataset.renders, this.dataset.mainParent, this.dataset.galleries, this.dataset.sectionId, this.dataset.productUrl, this.dataset.updateUrl, this.dataset.focusSelector ? this.dataset.focusSelector : null, this.dataset.mediaTemplate === 'true' ? true : false, this.dataset.mediaTemplate === 'true' ? this.dataset.template : null, this.dataset.loader ? this.dataset.loader : null, this.dataset.bundle === 'true' ? true : false);

    // Update .active-color / .selected-value in option label row when pills change
    const optionBlock = this.closest('.option-block');
    if (optionBlock) {
      const idx = this.dataset.optionIndex;
      const activeSpan = optionBlock.querySelector('.active-color[data-option-index="' + idx + '"]');
      if (activeSpan) activeSpan.textContent = value;
      const selectedSpan = optionBlock.querySelector('.selected-value[data-option-index="' + idx + '"]');
      if (selectedSpan) selectedSpan.textContent = value;
    }

    // Re-setup size buttons after variant change (in case DOM was updated)
    setTimeout(() => {
      this.setupSizeButtons();
    }, 100);
  }

  setOptions(elems, set) {
    const json = document.querySelector(this.dataset.jsonSelector);
    const data = JSON.parse(json.textContent);
    const variants = data.variants;
    const selectedOptionOneVariants = variants.filter(variant => set[0] === variant.option1);
    elems.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"]')];
      // Get previous option value - check for radio input first, then size button
      let previousOptionSelected = null;
      const previousRadio = elems[index - 1].querySelector('input[type="radio"]:checked');
      if (previousRadio) {
        previousOptionSelected = previousRadio.value;
      } else {
        // Check for active size button
        const previousSizeButton = elems[index - 1].querySelector('.size-button.active');
        if (previousSizeButton) {
          previousOptionSelected = previousSizeButton.dataset.size;
        } else {
          // Fallback to currentSet value
          previousOptionSelected = set[index - 1];
        }
      }
      if (!previousOptionSelected) return;

      const availableOptionInputsValue = selectedOptionOneVariants.filter(variant => variant.available && variant[`option${index}`] === previousOptionSelected).map(variantOption => variantOption[`option${index + 1}`]);
      this.setAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(elem => {
      const label = elem.nextElementSibling;
      const labelText = label.querySelector('[data-variant-status-text]');
      if (listOfAvailableOptions.includes(elem.getAttribute('value'))) {
        elem.classList.remove('disabled');
        labelText.classList.add('hidden'), labelText.classList.remove('visually-hidden');
        return;
      }
      elem.classList.add('disabled');
      labelText.classList.remove('hidden'), labelText.classList.add('visually-hidden');
    });
  }
}

class PacSection extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const mainProductSection = this.closest('.main-product-section');
    const productForm = this.closest('form.form_add_cartp');
    const qtyInput = (root) =>
      root?.querySelector('quantity-inputs input[name="quantity"]') || root?.querySelector('quantity-input input[name="quantity"]');
    if (mainProductSection) {
      this.quantityInput = qtyInput(mainProductSection);
      this.quantityWrapper = mainProductSection.querySelector('quantity-inputs');
    } else if (productForm) {
      this.quantityInput = qtyInput(productForm);
      this.quantityWrapper = productForm.querySelector('quantity-inputs');
    } else {
      this.quantityInput = qtyInput(document);
      this.quantityWrapper = document.querySelector('quantity-inputs');
    }

    if (this.quantityWrapper) {
      this.quantityWrapper.style.display = 'none';
    }

    this.addEventListener('click', this.handleClick.bind(this));

    // При загрузке активный пак
    const defaultActive = this.querySelector('.pack.active');
    if (defaultActive) {
      this.setQuantityFromPack(defaultActive);
    }
  }

  handleClick(event) {
    const clickedPack = event.target.closest('.pack');
    if (!clickedPack || !this.contains(clickedPack)) return;

    this.querySelectorAll('.pack').forEach(pack => {
      pack.classList.remove('active');
    });

    clickedPack.classList.add('active');
    this.setQuantityFromPack(clickedPack);
    // Вызываем кастомное событие (опционально)
    this.dispatchEvent(new CustomEvent('pack:change', {
      detail: {
        id: clickedPack.dataset.packId,
        quantity: clickedPack.querySelector('[data-packquntity]')?.dataset.packquntity
      },
      bubbles: true
    }));
  }

  setQuantityFromPack(packElement) {
    const quantityElement = packElement.querySelector('[data-packquntity]');
    const quantity = quantityElement?.dataset.packquntity;
    const mainProductSection = this.closest('.main-product-section');
    const productForm = this.closest('form.form_add_cartp');
    const root = mainProductSection || productForm || document;
    this.quantityInput = root.querySelector('quantity-inputs input[name="quantity"]') || root.querySelector('quantity-input input[name="quantity"]');
    if (this.quantityInput && quantity) {
      const qtyValue = parseInt(quantity, 10);
      if (!isNaN(qtyValue) && qtyValue > 0) {
        this.quantityInput.value = qtyValue;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    const onButtonClick = (event) => {
      event.preventDefault();
      const btn = event.target.closest('button');
      if (!btn) return;
      const previousValue = this.input.value;
      (btn.name === 'plus') ? this.input.stepUp() : this.input.stepDown();
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    const setBtnStatus = (type, value) => {
      const currVal = type === 'min' ? parseInt(this.input.min) : parseInt(this.input.max);
      const btn = type === 'min' ? this.querySelector("button[name='minus']") : this.querySelector("button[name='plus']");
      switch (type) {
        case 'min':
          btn.classList.toggle('disabled', value <= currVal);
          btn.toggleAttribute('disabled', value <= currVal);
          break;
        case 'max':
          btn.classList.toggle('disabled', value >= currVal);
          btn.toggleAttribute('disabled', value >= currVal);
          break;
        default:
          console.log('no button found');
          break;
      }
    }

    const setSubtotals = (qty, elem) => {
      const total = qty * parseInt(this.dataset.variantPrice);
      const formattedTotal = Shopify.formatMoney(total, window.money_format);
      if (elem) elem.innerHTML = formattedTotal;
      
      // Also update ATC button if it exists
      const atcButton = document.querySelector(`#ProductSubmitButton-${this.closest('.main-product-section')?.id || ''}-${this.closest('.main-product-section')?.dataset?.productId || ''}`);
      if (atcButton) {
        const atcPrice = atcButton.querySelector('.atc-button--price-total');
        if (atcPrice) {
          atcPrice.innerHTML = ' - ' + formattedTotal;
        }
      }
    }

    const onInputChange = () => {
      const value = parseInt(this.input.value);
      const qtySubtotal = this.querySelector('[data-qty-subtotal]');
      (this.input.min && setBtnStatus('min', value), this.input.max && setBtnStatus('max', value));
      if (!qtySubtotal) return;
      setSubtotals(value, qtySubtotal);
    }

    const setListeners = () => {
      this.changeEvent = new Event('input', { bubbles: true });
      this.inputChange = onInputChange.bind(this);
      this.buttonClick = onButtonClick.bind(this);
      this.input.addEventListener('input', this.inputChange);
      this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.buttonClick));
      
      document.addEventListener('variant:change', (event) => {
        const variant = event.detail.variant;
        if (variant) {
          this.dataset.variantPrice = variant.price;
          onInputChange();
        }
      });
    }

    const initialize = () => {
      this.input = this.querySelector('input');
      if (!this.input) return;
      setListeners();
      onInputChange();
    }
    initialize();
  }
}

class GiftBlock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const section = this.closest('.main-product-section');
    const form = section?.querySelector('form[data-type="add-to-cart-form"]') || section?.querySelector('form input[name="id"]')?.closest('form');
    this.input = form?.quantity || section?.querySelector('input[name="quantity"]') || section?.querySelector('quantity-input input[name="quantity"]') || document.querySelector('input[name="quantity"]');
    this.observeInputChanges();
    this.observeVariantChange();
    this.observePackChanges();
    this.checkGifts();
  }

  observeInputChanges() {
    if (!this.input) return;
    this.input.addEventListener('input', () => this.checkGifts());
    this.input.addEventListener('change', () => this.checkGifts());
  }

  observeVariantChange() {
    document.addEventListener('variant:change', () => this.checkGifts());
    const section = this.closest('.main-product-section');
    const variantInput = section?.querySelector('form input[name="id"]') || document.querySelector('#product-variant-id');
    if (variantInput) {
      variantInput.addEventListener('change', () => this.checkGifts());
    }
  }

  observePackChanges() {
    const section = this.closest('.main-product-section');
    const pacSection = section?.querySelector('pac-section') || document.querySelector('pac-section');
    if (pacSection) {
      pacSection.addEventListener('pack:change', () => this.checkGifts());
    }
  }

  async fetchCartData() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Failed to fetch cart');
      const data = await res.json();
      return {
        quantity: data.items.reduce((total, item) => total + item.quantity, 0),
        total_price: data.items.reduce((total, item) => total + item.quantity * item.final_price, 0) // accurate item pricing
      };
    } catch (e) {
      return { quantity: 0, total_price: 0 };
    }
  }

  getSelectedVariantPriceFromJson() {
    const section = this.closest('.main-product-section');
    const variantIdEl = section?.querySelector('form input[name="id"]') || document.querySelector('#product-variant-id');
    const jsonScript = document.querySelector('#json_prod_var');
    if (!variantIdEl || !jsonScript) return 0;

    try {
      const variantId = parseInt(variantIdEl.value, 10);
      const variants = JSON.parse(jsonScript.textContent);
      const selected = Array.isArray(variants) ? variants.find(v => v.id === variantId) : null;
      return selected && (selected.price != null) ? Number(selected.price) : 0;
    } catch (e) {
      return 0;
    }
  }

  async checkGifts() {
    const gifts = this.querySelectorAll('.gift');
    const cart = await this.fetchCartData();
    const section = this.closest('.main-product-section');
    const packEl = section?.querySelector('pac-section .pack.active [data-packquntity]');
    let localQty = parseInt(this.input?.value || '0', 10);
    if (packEl?.dataset?.packquntity) {
      const q = parseInt(packEl.dataset.packquntity, 10);
      if (!isNaN(q) && q > 0) localQty = q;
    } else {
      const form = section?.querySelector('form[data-type="add-to-cart-form"]') || section?.querySelector('form input[name="id"]')?.closest('form');
      const v = form?.quantity?.value;
      if (v != null && v !== '') localQty = parseInt(v, 10) || 1;
    }
    const selectedPrice = this.getSelectedVariantPriceFromJson();
    const localTotal = localQty * selectedPrice;
    const combinedQty = localQty + cart.quantity;
    const combinedTotal = localTotal + cart.total_price;

    gifts.forEach(gift => {
      const giftId = gift.dataset.giftid;
      if (!giftId || giftId === '0') return this.lockGift(gift);

      const area = gift.dataset.giftarea;
      const rule = gift.dataset.unlockrulle || 'price';
      const qtyUnlock = parseInt(gift.dataset.qtyunlock || '0', 10);
      const priceUnlock = parseInt(gift.dataset.priceunlock || '0', 10);

      let isUnlocked = false;

      if (area === 'global') {
        if (rule === 'quntity') {
          isUnlocked = qtyUnlock > 0 && combinedQty >= qtyUnlock;
        } else if (rule === 'price') {
          isUnlocked = priceUnlock > 0 && combinedTotal >= priceUnlock;
        }
      } else if (area === 'local') {
        if (rule === 'quntity') {
          isUnlocked = qtyUnlock > 0 && localQty >= qtyUnlock;
        } else if (rule === 'price') {
          isUnlocked = priceUnlock > 0 && localTotal >= priceUnlock;
        }
      }

      if (isUnlocked) {
        this.unlockGift(gift);
      } else {
        this.lockGift(gift);
      }
    });
  }
  unlockGift(gift) {
    gift.dataset.locked = 'false';
    const customImg = gift.dataset.giftimg?.trim();
    const productImg = gift.dataset.giftproductimg?.trim();

    // Check if URL is valid (not empty and has actual path before query params)
    const isValidUrl = (url) => {
      if (!url || url === '') return false;
      // Check for Liquid error messages
      if (url.includes('Liquid error') || url.includes('invalid url input')) return false;
      // Shopify returns URLs like "?width=400" when no image exists
      if (url.startsWith('?')) return false;
      // Check if there's actual content before the query params
      const pathPart = url.split('?')[0];
      return pathPart && pathPart.length > 0;
    };

    const imgSrc = isValidUrl(customImg) ? customImg :
      isValidUrl(productImg) ? productImg : null;

    const wrapper = gift.querySelector('.gift-image');

    if (imgSrc && wrapper) {
      // Add onerror handler to show placeholder if image fails to load
      wrapper.innerHTML = `<img src="${imgSrc}" alt="Gift" onerror="this.style.display='none'; this.parentElement.innerHTML='<svg width=\\'60\\' height=\\'60\\' viewBox=\\'0 0 60 60\\' fill=\\'none\\'><rect width=\\'60\\' height=\\'60\\' fill=\\'#f0f0f0\\'/><path d=\\'M30 20v20M20 30h20\\' stroke=\\'#999\\' stroke-width=\\'2\\'/></svg><div style=\\'color: #666; font-size: 12px; margin-top: 8px;\\'>Gift Image</div>';">`;
    } else if (wrapper) {
      // No valid image URL, show placeholder
      wrapper.innerHTML = `<svg width="60" height="60" viewBox="0 0 60 60" fill="none"><rect width="60" height="60" fill="#f0f0f0"/><path d="M30 20v20M20 30h20" stroke="#999" stroke-width="2"/></svg><div style="color: #666; font-size: 12px; margin-top: 8px;">Gift Image</div>`;
    }
  }


  lockGift(gift) {
    gift.dataset.locked = 'true';
    const wrapper = gift.querySelector('.gift-image');
    const maney = gift.dataset.priceunlock;
    const customLockImg = this.dataset.lockimg?.trim();

    if (wrapper) {
      if (customLockImg && customLockImg !== '') {
        // Use custom lock image
        wrapper.innerHTML = this.lockCustomImage(customLockImg, maney);
      } else {
        // Use default SVG lock icon
        wrapper.innerHTML = this.lockSVG(maney);
      }
    }
  }

  lockCustomImage(imgSrc, maney) {
    return `<img src="${imgSrc}" alt="Locked" style="max-width: 100px; max-height: 100px;">
            <div>Spend ${Shopify.formatMoney(maney, window.money_format || '{{amount}}')} to unlock</div>`;
  }

  lockSVG(maney) {
    return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="23.5" fill="white" stroke="black"/>
            <path d="M30.75 34.3184H17.25C16.6533 34.3184 16.081 34.0813 15.659 33.6593C15.2371 33.2374 15 32.6651 15 32.0684V23.8184C15 23.2216 15.2371 22.6493 15.659 22.2274C16.081 21.8054 16.6533 21.5684 17.25 21.5684H30.75C31.3467 21.5684 31.919 21.8054 32.341 22.2274C32.7629 22.6493 33 23.2216 33 23.8184V32.0684C33 32.6651 32.7629 33.2374 32.341 33.6593C31.919 34.0813 31.3467 34.3184 30.75 34.3184ZM17.25 23.0684C17.0511 23.0684 16.8603 23.1474 16.7197 23.288C16.579 23.4287 16.5 23.6194 16.5 23.8184V32.0684C16.5 32.2673 16.579 32.458 16.7197 32.5987C16.8603 32.7393 17.0511 32.8184 17.25 32.8184H30.75C30.9489 32.8184 31.1397 32.7393 31.2803 32.5987C31.421 32.458 31.5 32.2673 31.5 32.0684V23.8184C31.5 23.6194 31.421 23.4287 31.2803 23.288C31.1397 23.1474 30.9489 23.0684 30.75 23.0684H17.25Z" fill="#101820"/>
            <path d="M30 23.0684H18C17.8011 23.0684 17.6103 22.9893 17.4697 22.8487C17.329 22.708 17.25 22.5173 17.25 22.3184V17.8184C17.25 16.2271 17.8821 14.7009 19.0074 13.5757C20.1326 12.4505 21.6587 11.8184 23.25 11.8184H24.75C26.3413 11.8184 27.8674 12.4505 28.9926 13.5757C30.1179 14.7009 30.75 16.2271 30.75 17.8184V22.3184C30.75 22.5173 30.671 22.708 30.5303 22.8487C30.3897 22.9893 30.1989 23.0684 30 23.0684ZM18.75 21.5684H29.25V17.8184C29.25 16.6249 28.7759 15.4803 27.932 14.6364C27.0881 13.7925 25.9435 13.3184 24.75 13.3184H23.25C22.0565 13.3184 20.9119 13.7925 20.068 14.6364C19.2241 15.4803 18.75 16.6249 18.75 17.8184V21.5684Z" fill="#101820"/>
            <path d="M24 28.3184C23.7033 28.3184 23.4133 28.2304 23.1666 28.0656C22.92 27.9007 22.7277 27.6665 22.6142 27.3924C22.5006 27.1183 22.4709 26.8167 22.5288 26.5257C22.5867 26.2348 22.7296 25.9675 22.9393 25.7577C23.1491 25.5479 23.4164 25.4051 23.7074 25.3472C23.9983 25.2893 24.2999 25.319 24.574 25.4325C24.8481 25.5461 25.0824 25.7383 25.2472 25.985C25.412 26.2317 25.5 26.5217 25.5 26.8184C25.5 27.2162 25.342 27.5977 25.0607 27.879C24.7794 28.1603 24.3978 28.3184 24 28.3184Z" fill="#101820"/>
            <path d="M24.75 27.5684H23.25V30.5684H24.75V27.5684Z" fill="#101820"/>
          </svg>
          <div>Spend ${Shopify.formatMoney(maney, window.money_format || '{{amount}}')} to unlock</div>
          `;
  }
}

// Header cart badge updates live in header-cart-badge.js (window.updateCartIconDirectly).

/** Packs + form quantity: use pac-section active pack qty when present, else form quantity. */
function getAtcMainQty(form) {
  const section = form?.closest('.main-product-section');
  const packEl = section?.querySelector('pac-section .pack.active [data-packquntity]');
  if (packEl?.dataset?.packquntity) {
    const q = parseInt(packEl.dataset.packquntity, 10);
    if (!isNaN(q) && q > 0) return q;
  }
  const v = form?.quantity?.value;
  return (v != null && v !== '') ? (parseInt(v, 10) || 1) : 1;
}

function surgeIsPetQuantityOptionName(optionName) {
  if (optionName == null || optionName === '') return false;
  const n = String(optionName).trim().toLowerCase();
  if (!n) return false;
  if (/\bpet(s)?\b/.test(n)) return true;
  if (/number\s+of\s+pets?/.test(n)) return true;
  if (/pet\s*(qty|quantity|count)/.test(n)) return true;
  if (/how\s+many\s+pets?/.test(n)) return true;
  const petTitle = document.querySelector('.variant-element_title--pet-qty');
  if (petTitle) {
    const label = petTitle.textContent.replace(/\*/g, '').trim().toLowerCase();
    if (label && n === label) return true;
  }
  return false;
}

function surgeIsPetQuantityOptionValue(val, optionName) {
  if (surgeIsPetQuantityOptionName(optionName)) return true;
  const s = String(val || '').trim();
  if (!s) return false;
  if (/\bpet(s)?\b/i.test(s)) return true;
  if (/^\d+\s*[-–]?\s*pet(s)?\b/i.test(s)) return true;
  return false;
}

function surgeSkipAtcToastOption(val, optionName) {
  const s = String(val || '').trim();
  return !s || s === 'Default Title' || surgeIsPetQuantityOptionValue(s, optionName);
}

/**
 * @param {Set<string>} seen
 * @param {string[]} parts
 * @param {unknown} val
 * @param {string | undefined} optionName
 */
function surgePushAtcToastPart(seen, parts, val, optionName) {
  const s = String(val || '').trim();
  if (surgeSkipAtcToastOption(s, optionName)) return;
  const key = s.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  parts.push(s);
}

/**
 * @param {ParentNode} root
 * @returns {string[]}
 */
function surgeCollectAddedToCartPartsFromDom(root) {
  const parts = [];
  const seen = new Set();

  const pickers = Array.from(
    root.querySelectorAll('variant-pills[data-option-index], variant-dropdown[data-option-index]')
  ).sort(
    (a, b) =>
      parseInt(a.dataset.optionIndex || '0', 10) - parseInt(b.dataset.optionIndex || '0', 10)
  );

  pickers.forEach((picker) => {
    if (
      picker.querySelector(
        '.pet-qty-segmented-card, .pet-qty-segment, .pet-qty-segmented-trigger--sr, .pet-qty-segmented-options'
      ) ||
      picker.closest('.pet-qty-segmented-card')
    ) {
      return;
    }

    const optIdx = parseInt(picker.dataset.optionIndex || '-1', 10);
    const optName = Number.isFinite(optIdx) ? window.productOptions?.[optIdx] : undefined;
    if (surgeIsPetQuantityOptionName(optName)) return;

    const selectedSpan =
      picker.closest('.option-block, .option-group, .cc-pdp-bg-option')?.querySelector(
        `.selected-value[data-option-index="${optIdx}"]`
      ) || root.querySelector(`.selected-value[data-option-index="${optIdx}"]`);
    if (selectedSpan?.textContent?.trim()) {
      surgePushAtcToastPart(seen, parts, selectedSpan.textContent, optName);
      return;
    }

    const checked = picker.querySelector('input[type="radio"]:checked, input:checked');
    if (checked?.value) {
      surgePushAtcToastPart(seen, parts, checked.value, optName);
      return;
    }

    const selectedOpt = picker.querySelector('.option-value.selected');
    if (selectedOpt?.dataset?.value) {
      surgePushAtcToastPart(seen, parts, selectedOpt.dataset.value, optName);
      return;
    }

    const trigger = picker.querySelector(
      '.custom-select .select-trigger:not(.pet-qty-segmented-trigger--sr)'
    );
    if (trigger) {
      const label = (trigger.textContent || '').trim();
      if (label && !/^select\b/i.test(label)) {
        surgePushAtcToastPart(seen, parts, label, optName);
      }
    }
  });

  const styleSelect = root.querySelector('.native-style-select');
  if (styleSelect && !styleSelect.classList.contains('placeholder-active') && styleSelect.value) {
    const styleLabel =
      styleSelect.options[styleSelect.selectedIndex]?.text?.trim() ||
      String(styleSelect.value).trim();
    surgePushAtcToastPart(seen, parts, styleLabel, 'Style');
  }

  return parts;
}

/**
 * @param {{ options?: string[] } | null | undefined} variant
 * @returns {string[]}
 */
function surgeCollectAddedToCartPartsFromVariant(variant) {
  const parts = [];
  const seen = new Set();
  const optionNames = window.productOptions || [];
  (variant?.options || []).forEach((val, i) => {
    surgePushAtcToastPart(seen, parts, val, optionNames[i]);
  });
  return parts;
}

/**
 * Build cart toast copy: "Added — Pink Dream, M, Short Sleeve Set"
 * @param {HTMLFormElement | null | undefined} form
 * @param {ParentNode | null | undefined} scopeRoot
 * @returns {string}
 */
function surgeBuildAddedToCartMessage(form, scopeRoot) {
  const root =
    scopeRoot ||
    form?.closest('#surge-combined-listing-shell') ||
    form?.closest('.main-product-section') ||
    document;
  const variantId = form?.querySelector('[name="id"]')?.value;
  const variant = window.productVariants?.find((v) => String(v.id) === String(variantId));
  const seen = new Set();
  const parts = [];

  const domParts = surgeCollectAddedToCartPartsFromDom(root);
  const variantParts = surgeCollectAddedToCartPartsFromVariant(variant);
  const sourceParts = domParts.length ? domParts : variantParts;

  sourceParts.forEach((p) => {
    const key = p.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    parts.push(p);
  });

  if (domParts.length && variantParts.length) {
    variantParts.forEach((p) => {
      const key = p.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      parts.push(p);
    });
  }

  let colorName = surgePjExtrasSelectedBackgroundColor(root);
  if (!colorName) colorName = surgePjExtrasDeriveBackgroundFromTitle(root);
  if (colorName) {
    const hasColor = parts.some((p) => p.toLowerCase() === colorName.toLowerCase());
    if (!hasColor) parts.unshift(colorName);
  }

  if (!parts.length) {
    const h1 = root.querySelector('h1.custom-product-title, h1');
    if (h1) {
      const title = h1.textContent.trim();
      if (title) parts.push(title);
    }
  }

  const detail = parts.join(', ');
  return detail ? `Added — ${detail}` : 'Added to your cart';
}

/** Shopify /cart/add requires a numeric variant id (> 0). */
function surgeCoerceCartVariantId(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  const m = s.match(/(\d+)$/);
  const n = parseInt(m ? m[1] : s, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Line-item properties for the main PDP line only (not FBT cross-sell / extras). */
function isMainPdpPropertyInput(input) {
  if (!input || !input.name) return false;
  if (input.closest('.pajama-extras-slider')) return false;
  if (input.closest('[data-cross-sell-slide]')) return false;
  if (input.closest('.cross-sell-main-container')) return false;
  return true;
}

/** Root element for ATC extras/addons (combined-listing shell when present). */
function surgePjExtrasAtcAddonRoot(form) {
  return (
    form?.closest('#surge-combined-listing-shell') ||
    form?.closest('.main-product-section') ||
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section') ||
    document
  );
}

function surgePjExtrasGetActiveBuyboxRoot() {
  return (
    document.getElementById('surge-combined-listing-shell') ||
    document.querySelector('.main-product-section') ||
    document.body
  );
}

function surgePjExtrasParseExtrasConfig(root) {
  const jsonEl = root.querySelector('.pj-extras-config-json');
  if (!jsonEl) return null;
  try {
    return JSON.parse(jsonEl.textContent || 'null');
  } catch (e) {
    return null;
  }
}

function surgePjExtrasDeriveBackgroundFromTitle(root) {
  const h1 = root.querySelector('h1.custom-product-title') || root.querySelector('h1');
  const title = h1 ? h1.textContent.trim() : '';
  const parts = String(title).split(/\s*-\s*/);
  return (parts[1] || '').trim();
}

function surgePjExtrasSelectedBackgroundColor(root) {
  const activeSwatch =
    root.querySelector('input[name*="-Color-"]:checked') ||
    root.querySelector('input[name*="-Background-"]:checked') ||
    root.querySelector('input[name*="Color"]:checked') ||
    root.querySelector('.variant-options-wrap input:checked') ||
    root.querySelector('variant-pills input[type="radio"]:checked');
  if (activeSwatch && activeSwatch.value) return String(activeSwatch.value).trim();
  return '';
}

/** Sync pj-bandana checkbox variant id + hidden color from PDP background (survives combined-listing shell swaps). */
function surgeRefreshPajamaExtrasBandanaValues(root) {
  root = root || surgePjExtrasGetActiveBuyboxRoot();
  const extrasData = surgePjExtrasParseExtrasConfig(root);
  const bandanaProduct = extrasData?.extras_products?.find((p) => p.identifier === 'pj-bandana') || null;
  if (!bandanaProduct?.variants?.length) return;
  const bandanaVariants = bandanaProduct.variants;
  const bandanaCheckbox = root.querySelector('input.extraOption[data-identifier="pj-bandana"]');
  const bandanaHiddenInput = root.querySelector('.backgroundHiddenInputBandana');
  if (!bandanaCheckbox || !bandanaHiddenInput) return;

  let selectedBg = surgePjExtrasSelectedBackgroundColor(root);
  if (!selectedBg) {
    selectedBg = surgePjExtrasDeriveBackgroundFromTitle(root);
  }
  const mainBg = root.querySelector('.backgroundHiddenInput:not(.backgroundHiddenInputBandana)');
  if (!selectedBg && mainBg && String(mainBg.value || '').trim()) {
    selectedBg = String(mainBg.value).trim();
  }

  let matchedVariant;
  if (selectedBg) {
    matchedVariant = bandanaVariants.find(
      (v) => String(v.name).toLowerCase() === selectedBg.toLowerCase()
    );
  }
  const use = matchedVariant || bandanaVariants[0];
  bandanaCheckbox.value = String(use.id);
  bandanaHiddenInput.value = use.name;

  const sel = root.querySelector('.pajama-extras-slider__bandana-select');
  if (sel && matchedVariant) sel.value = String(matchedVariant.id);
}

function surgeWrapSelectBackgroundForBandana() {
  if (window.__surgeBandanaSelectBgWrapped) return;
  if (typeof window.selectBackground !== 'function') return;
  window.__surgeBandanaSelectBgWrapped = true;
  const orig = window.selectBackground;
  window.selectBackground = function (name, value, key, showRelatedImages, sku) {
    orig.apply(this, arguments);
    surgeRefreshPajamaExtrasBandanaValues();
  };
}

function surgeInitPajamaExtrasBandanaSync() {
  if (window.__surgePajamaExtrasBandanaSyncInit) return;
  window.__surgePajamaExtrasBandanaSyncInit = true;

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (
      t.closest('.variant-options-wrap') ||
      (t.matches('input') && typeof t.name === 'string' && t.name.includes('Color'))
    ) {
      surgeRefreshPajamaExtrasBandanaValues();
      return;
    }
    if (t.matches('input.extraOption[data-identifier="pj-bandana"]')) {
      const root =
        t.closest('#surge-combined-listing-shell') ||
        t.closest('.main-product-section') ||
        surgePjExtrasGetActiveBuyboxRoot();
      const hidden = root.querySelector('.backgroundHiddenInputBandana');
      if (t.checked) surgeRefreshPajamaExtrasBandanaValues(root);
      else if (hidden) hidden.value = '';
    }
  });

  document.addEventListener('variant:change', () => {
    surgeRefreshPajamaExtrasBandanaValues();
  });

  document.addEventListener('surge:combined-listing-shell-replaced', () => {
    surgeRefreshPajamaExtrasBandanaValues();
  });

  surgeWrapSelectBackgroundForBandana();
  document.addEventListener('DOMContentLoaded', surgeWrapSelectBackgroundForBandana);
  window.addEventListener('load', surgeWrapSelectBackgroundForBandana);
  setTimeout(surgeWrapSelectBackgroundForBandana, 2500);
}

surgeInitPajamaExtrasBandanaSync();

/** Selected addon variant ids (excluding placeholders). */
function getAtcAddonItems(form) {
  const root = surgePjExtrasAtcAddonRoot(form);
  const items = [];

  // Standard theme addons
  root.querySelectorAll('.product-addons__toggle:checked:not(:disabled)').forEach((cb) => {
    const item = cb.closest('.product-addons__item');
    if (item?.classList.contains('product-addons__item--placeholder')) return;
    const id = surgeCoerceCartVariantId(item?.dataset?.variantId);
    if (id) items.push({ id });
  });

  // Extra options (Rush, Gift Box, Bandana, ...) from the cuddleclones.extras metafield. Includes the
  // FBT slider's add-ons: its "Add to order" button ticks the add-on's checkbox and then clicks this
  // form's submit, so the add-on lands in the same /cart/add.js call as the parent and inherits its
  // _unique_key. Cross-sell cards are not .extraOption inputs — they stage separately and are collected
  // by __surgeCrossSellGetAddonItems below.
  root.querySelectorAll('.extraOption:checked').forEach((cb) => {
    const id = surgeCoerceCartVariantId(cb.value);
    if (!id) return;

    const props = {};
    if (cb.dataset.rushKey) {
      props['_rush_key'] = cb.dataset.rushKey;
    }

    // Add unique linking keys to add-ons
    const originalKeyInput = document.getElementById('originalkey');
    if (originalKeyInput && originalKeyInput.value) {
      if (cb.dataset.identifier === "rush") {
        // Rush add-on must carry _unique_key (matching the main line's
        // _original_unique_key) so cart bundle-removal groups it with the
        // product. _original_unique_key belongs only on the main line.
        props['_unique_key'] = originalKeyInput.value;
      } else if (cb.dataset.identifier === "pj-bandana") {
        props['_unique_key'] = originalKeyInput.value;
        const bandanaBg = cb.closest('.pajama-extras-slider__cta, .optionCheckboxContainer')?.querySelector('.backgroundHiddenInputBandana');
        if (bandanaBg && String(bandanaBg.value || '').trim()) {
          props['Background Color'] = String(bandanaBg.value).trim();
        }
      } else {
        props['_unique_key'] = originalKeyInput.value;
      }
    }

    items.push({ id, properties: props });
  });

  const crossSellKeyInput = document.getElementById('originalkey');
  const crossSellBundleKey =
    crossSellKeyInput && crossSellKeyInput.value
      ? String(crossSellKeyInput.value).trim()
      : '';
  if (
    crossSellBundleKey &&
    typeof window.__surgeCrossSellGetAddonItems === 'function'
  ) {
    const crossSellLines = window.__surgeCrossSellGetAddonItems(crossSellBundleKey);
    if (Array.isArray(crossSellLines) && crossSellLines.length) {
      crossSellLines.forEach((row) => {
        const vid = row && surgeCoerceCartVariantId(row.id);
        if (vid) items.push({ id: vid, properties: row.properties || {} });
      });
    }
  }

  return items;
}

/** Unlocked gift variant ids in the form's product section. */
function getAtcGiftIds(form) {
  const section = form?.closest('.main-product-section');
  const ids = [];
  section?.querySelectorAll('.gift[data-locked="false"]')?.forEach((el) => {
    const id = surgeCoerceCartVariantId(el.dataset?.giftid);
    if (id) ids.push(id);
  });
  return ids;
}

// Global wrapper to update button total from anywhere
function updateButtonTotal() {
  const atcSubmit = document.querySelector('atc-submit');
  if (atcSubmit && typeof atcSubmit.updateButtonTotal === 'function') {
    atcSubmit.updateButtonTotal();
  }
}

class AtcSubmit extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = document.querySelector(this.dataset.form);
    if (!this.form) return;
    this.recipientForm = this.form.querySelector('recipient-form');
    this.submit = this.onAddToCart.bind(this);
    this.form.addEventListener('submit', this.submit);

    const pendingBtn = this.form.querySelector('[name="add"][data-atc-pending-enable="true"]');
    if (pendingBtn) {
      pendingBtn.removeAttribute('disabled');
      pendingBtn.removeAttribute('data-atc-pending-enable');
    }

    // Update button total on changes
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', () => this.updateButtonTotal());
      quantityInput.addEventListener('input', () => this.updateButtonTotal());
    }

    // Global listener for dynamic elements (addons, rush, pet qty)
    document.addEventListener('change', (e) => {
      if (e.target.closest('.product-addons__toggle') ||
          e.target.closest('.extraOption_rush') ||
          e.target.closest('.quantity_button') ||
          // FBT slider add-on toggled via "Add to order" — its price is part of the button total.
          e.target.closest('.pajama-extras-slider .extraOption') ||
          e.target.name === 'quantity') {
        this.updateButtonTotal();
      }
    });

    // Handle clicks for non-input UI elements (segmented pet qty, quantity buttons)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.pet-qty-segment') || 
          e.target.closest('.quantity_button')) {
        setTimeout(() => this.updateButtonTotal(), 20);
      }
    });

    document.addEventListener('variant:change', (e) => {
      // Small delay to ensure other listeners (like QuantityInput) have run if they need to
      setTimeout(() => this.updateButtonTotal(), 10);
    });
    this.updateButtonTotal();
  }

  showNotification(message, type = 'info') {
    const cartDrawer = document.querySelector('cart-slide.side-cart, cart-slide');
    const mountInDrawer = Boolean(cartDrawer?.classList.contains('active'));
    const mountTarget = mountInDrawer ? cartDrawer : document.body;

    let toast = document.getElementById('surge-pdp-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'surge-pdp-toast';
      toast.className = 'surge-toast';
      toast.innerHTML =
        '<div class="surge-toast-check">' +
          '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M2 7 L5 10 L12 3"/></svg>' +
        '</div>' +
        '<span class="surge-toast-message"></span>';
      mountTarget.appendChild(toast);
    } else if (toast.parentElement !== mountTarget) {
      mountTarget.appendChild(toast);
    }

    toast.classList.toggle('surge-toast--drawer', mountInDrawer);

    const messageEl = toast.querySelector('.surge-toast-message');
    if (messageEl) messageEl.textContent = message;

    const isWarning = type === 'warning' || type === 'error';
    toast.style.background = isWarning ? '#B048B5' : '#111827';
    toast.classList.add('show');

    if (window._surgeToastTimeout) clearTimeout(window._surgeToastTimeout);
    window._surgeToastTimeout = setTimeout(() => toast.classList.remove('show'), 2800);
  }

  showFieldError(element, message) {
    this.showNotification(message, 'warning');

    const creationContainer = element?.closest?.('.rushExtraOption-container--pajamas') ||
      (element?.classList?.contains('rushExtraOption-container--pajamas') ? element : null);

    if (creationContainer) {
      const stdCard = creationContainer.querySelector('[data-creation="standard"]');
      const rushCard = creationContainer.querySelector('[data-creation="rush"]');
      if (stdCard) {
        stdCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      [stdCard, rushCard].filter(Boolean).forEach((card) => {
        card.style.borderColor = '#CB62D0';
        setTimeout(() => {
          card.style.borderColor = '';
        }, 1800);
      });
      return;
    }

    const petQtyField = element?.closest?.('.pet-qty-segmented-card') ||
      element?.querySelector?.('.pet-qty-segmented-card') ||
      (element?.classList?.contains('pet-qty-segmented-card') ? element : null) ||
      document.getElementById('petButtons') ||
      document.querySelector('.pet-qty-segmented-card');

    if (petQtyField) {
      petQtyField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const scrollTarget = element?.closest?.('.multi-image-uploader') ||
      element?.closest?.('.single-image-uploader') ||
      element;

    if (scrollTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  clearFieldErrors(container) {
    if (!container) return;
    container.querySelectorAll('.surge-field-error').forEach((el) => el.remove());
  }

  validateStyleSelection(productSection) {
    const styleSelect = productSection?.querySelector('.native-style-select');
    if (!styleSelect || !styleSelect.classList.contains('placeholder-active')) return true;

    this.showNotification('Please select a style first', 'warning');
    styleSelect.classList.add('style-select-flash-error');
    styleSelect.focus();
    setTimeout(() => styleSelect.classList.remove('style-select-flash-error'), 2000);
    return false;
  }

  validateMandatoryCustomOptions(productSection) {
    if (!productSection || typeof customOptions === 'undefined' || !customOptions) return true;

    for (const key in customOptions) {
      if (!Object.prototype.hasOwnProperty.call(customOptions, key)) continue;
      const opt = customOptions[key];
      if (opt.field !== 'mandatory' || !opt.display) continue;

      const hiddenInput = productSection.querySelector(`.${opt.name}HiddenInput`);
      if (!hiddenInput) continue;

      const val = String(hiddenInput.value || '').trim();
      if (val !== '' && val !== 'not-selected') continue;

      const anchor =
        productSection.querySelector(`.${opt.name}Option`) ||
        productSection.querySelector(`[data-variant-option-name="${opt.name}"]`)?.closest('.selector-wrapper') ||
        hiddenInput;

      const label = opt.heading || opt.name || 'option';
      this.showFieldError(anchor, `Please select ${label} first`);
      return false;
    }
    return true;
  }

  validateOpenVariantDropdowns(productSection) {
    if (!productSection) return true;

    const triggers = productSection.querySelectorAll(
      'variant-dropdown .custom-select .select-trigger:not(.pet-qty-segmented-trigger--sr)'
    );

    for (const trigger of triggers) {
      if (trigger.closest('.style-select-wrapper')) continue;
      const label = (trigger.textContent || '').trim();
      if (label !== 'Select option' && !/^select\b/i.test(label)) continue;

      const field = trigger.closest('.custom-select') || trigger.closest('variant-dropdown') || trigger;
      const optionTitle =
        field?.closest('.option-group')?.querySelector('.variant-element_title')?.textContent?.trim() ||
        'all options';
      this.showFieldError(field, `Please select ${optionTitle} first`);
      return false;
    }
    return true;
  }

  /** Combined-listing/color swatches: block ATC when a color option exists but none is selected. */
  validateColorSelection(productSection) {
    if (!productSection) return true;

    const colorPickers = productSection.querySelectorAll(
      'variant-pills[data-combined-sibling-option="true"], variant-pills.color-swatches'
    );

    for (const picker of colorPickers) {
      // Only enforce when this picker actually renders selectable swatches.
      if (!picker.querySelector('input[type="radio"]')) continue;
      if (picker.querySelector('input[type="radio"]:checked')) continue;

      // Toast + scroll straight to the labeled color section (the option-group that
      // holds the "Color" heading and swatches). We scroll here directly rather than
      // via showFieldError, whose scroll routing has a document-wide pet-qty fallback
      // that would hijack the scroll to a pet-qty card when one exists on the page.
      this.showNotification('Please select color', 'warning');

      const colorSection = picker.closest('.option-group') || picker;
      colorSection.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return false;
    }
    return true;
  }

  /** PDP has `uploader` tag (single-image-uploader) but not `multi-uploader` — no pet-qty option. */
  isSingleUploaderOnly(productSection) {
    if (!productSection) return false;
    return (
      !!productSection.querySelector('.single-image-uploader') &&
      !productSection.querySelector('.multi-image-uploader')
    );
  }

  validatePetQtyAndPhotos(productSection) {
    if (this.isSingleUploaderOnly(productSection)) {
      const singleUploader = productSection.querySelector('.single-image-uploader');
      const hiddenInput =
        document.getElementById('hiddenInputSimple') ||
        productSection.querySelector('#hiddenInputSimple');
      const imageUrl = hiddenInput?.value?.trim();
      if (!imageUrl || imageUrl === 'null') {
        this.showFieldError(singleUploader, 'Please upload a photo for your pet');
        return false;
      }
      return true;
    }

    const uploaderContainer =
      productSection?.querySelector('.multi-image-uploader') ||
      productSection?.querySelector('.single-image-uploader');
    if (!uploaderContainer) return true;

    const variantId = this.form.querySelector('input[name="id"]')?.value;
    const variant = window.productVariants?.find((v) => v.id == variantId);
    let petCount = 0;

    const petCountInput =
      this.form.querySelector('input[name="properties[Number of Pets]"]') ||
      this.form.querySelector('input[name="properties[number of pets]"]');
    if (petCountInput?.value) {
      petCount = parseInt(petCountInput.value, 10) || 0;
    }

    const petQtyLabel = productSection.querySelector('.variant-element_title--pet-qty');
    const petQtyField =
      petQtyLabel?.closest('variant-dropdown, variant-pills') ||
      productSection.querySelector('.pet-qty-segmented-card');

    if (petCount <= 0 && petQtyField) {
      const segmentedSelect = petQtyField.querySelector('.custom-select--pet-qty-segmented') || (petQtyField.classList.contains('custom-select--pet-qty-segmented') ? petQtyField : null);
      if (!segmentedSelect || segmentedSelect.classList.contains('user-has-selected')) {
        const activeItem =
          petQtyField.querySelector('.selected') ||
          petQtyField.querySelector('.active') ||
          petQtyField.querySelector('[aria-selected="true"]') ||
          petQtyField.querySelector('.pet-qty-segment.selected');

        if (activeItem) {
          const text =
            activeItem.value ||
            activeItem.textContent ||
            activeItem.closest('label')?.textContent ||
            '';
          const match = text.match(/(\d+)/);
          if (match) petCount = parseInt(match[1], 10);
        }
      }
    }

    if (petCount <= 0 && !petQtyField && variant) {
      const PET_KEYWORDS = ['pet', 'pets', 'pet quantity', 'pet count'];
      variant.options.forEach((opt) => {
        if (petCount > 0) return;
        const lowerOpt = String(opt).toLowerCase();
        PET_KEYWORDS.forEach((kw) => {
          if (lowerOpt.includes(kw)) {
            const match = lowerOpt.match(/(\d+)/);
            if (match) petCount = parseInt(match[1], 10);
          }
        });
      });
    }

    if (petCount <= 0 && !petQtyField) {
      const uploaderSections = productSection.querySelectorAll('.uploaderSection');
      if (uploaderSections.length > 0) {
        petCount = uploaderSections.length;
      }
    }

    const multiUploader = productSection.querySelector('.multi-image-uploader');
    if (multiUploader && multiUploader.innerHTML.trim() === '') {
      petCount = 0;
    }

    if (petCount <= 0) {
      this.showFieldError(petQtyField || uploaderContainer, 'Please choose number of pets first');
      return false;
    }

    const allPossibleInputs = [
      ...Array.from(productSection.querySelectorAll('input[name*="_image_url"]')),
      ...Array.from(this.form.querySelectorAll('input[name*="_image_url"]')),
      ...Array.from(document.querySelectorAll('input[name*="_image_url"]')),
    ];

    const uniqueInputsMap = new Map();
    allPossibleInputs.forEach((input) => uniqueInputsMap.set(input.name, input));

    let uploadedPhotos = 0;
    uniqueInputsMap.forEach((input) => {
      if (input.value && input.value.trim() !== '' && input.value !== 'null') {
        uploadedPhotos++;
      }
    });

    if (uploadedPhotos < petCount) {
      const msg =
        petCount === 1
          ? 'Please upload a photo for your pet'
          : `Please upload a photo for each of your ${petCount} pets (Detected: ${uploadedPhotos}/${petCount})`;
      this.showFieldError(uploaderContainer, msg);
      return false;
    }

    return true;
  }

  onAddToCart(event) {
    event.preventDefault();
    this.error = false;

    const productSection = this.closest('.main-product-section');
    this.clearFieldErrors(productSection);

    if (!this.validateStyleSelection(productSection)) return;
    if (!this.validateMandatoryCustomOptions(productSection)) return;
    if (!this.validateOpenVariantDropdowns(productSection)) return;
    if (!this.validateColorSelection(productSection)) return;
    if (!this.validatePetQtyAndPhotos(productSection)) return;
    const creationContainer = productSection?.querySelector('.rushExtraOption-container--pajamas');
    if (creationContainer) {
      const stdCard = creationContainer.querySelector('[data-creation="standard"]');
      const rushCard = creationContainer.querySelector('[data-creation="rush"]');
      if (stdCard && rushCard && !stdCard.classList.contains('active') && !rushCard.classList.contains('active')) {
        this.showFieldError(creationContainer, 'Please choose a creation time');
        return;
      }
    }

    const secArr = JSON.parse(this.dataset.sections);
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const mainId = this.form.querySelector('[name="id"]')?.value;
    const mainVariantId = surgeCoerceCartVariantId(mainId);
    const mainQty = getAtcMainQty(this.form);
    let originalKey = document.getElementById('originalkey');
    if (originalKey) {
      originalKey.value = Date.now();
    }
    surgeRefreshPajamaExtrasBandanaValues(
      this.form?.closest('#surge-combined-listing-shell') ||
        this.form?.closest('.main-product-section') ||
        undefined
    );
    const addonItems = getAtcAddonItems(this.form);
    const giftIds = getAtcGiftIds(this.form);
    const hasExtraItems = addonItems.length > 0 || giftIds.length > 0;

    if (!mainVariantId) {
      atcError(
        {
          description:
            'Please select all required product options (including size and style) before adding to cart.',
        },
        this,
        true
      );
      return;
    }

    // -- START OF PAYLOAD CONSTRUCTION LOGIC --
    const section = this.closest('.main-product-section');
    const customProperties = section
      ? section.querySelectorAll('[name^="properties["]')
      : [];
    let formData;

    if (hasExtraItems) {
      formData = new FormData();
      formData.append('items[][id]', String(mainVariantId));
      formData.append('items[][quantity]', String(mainQty));

      // Append properties for the main item within the items array branch
      customProperties.forEach((input) => {
        if (input.disabled) return;
        if (!isMainPdpPropertyInput(input)) return;
        const propName = input.name.match(/properties\[(.*?)\]/);
        if (propName && propName[1] && input.value !== '') {
          formData.append(`items[][properties][${propName[1]}]`, input.value);
        }
      });

      addonItems.forEach((item) => {
        const vid = surgeCoerceCartVariantId(item.id);
        if (!vid) return;
        formData.append('items[][id]', String(vid));
        formData.append('items[][quantity]', '1');
        if (item.properties) {
          for (const key in item.properties) {
            const val = item.properties[key];
            if (val != null && String(val).trim() !== '') {
              formData.append(`items[][properties][${key}]`, val);
            }
          }
        }
      });
      giftIds.forEach((id) => {
        formData.append('items[][id]', String(id));
        formData.append('items[][quantity]', '1');
      });
      formData.append('sections', secArr.map(s => s.id));
      formData.append('sections_url', window.location.pathname);
    } else {
      formData = new FormData(this.form);
      formData.set('id', String(mainVariantId));
      formData.set('quantity', String(mainQty));

      // Append properties for the main item in the simple flat branch
      customProperties.forEach((input) => {
        if (input.disabled) return;
        if (!isMainPdpPropertyInput(input)) return;
        if (input.value !== '' && !formData.has(input.name)) {
          formData.append(input.name, input.value);
        }
      });

      formData.append('sections', secArr.map(s => s.id));
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;
    this._surgeLastAddedMessage = surgeBuildAddedToCartMessage(this.form, productSection);
    fetchAtc(secArr, config, this, this.dataset.loader);
  }

  updateButtonTotal() {
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    const variantId = this.form.querySelector('input[name="id"]')?.value;
    const productSection = this.closest('.main-product-section');
    
    let basePrice = 0;
    if (window.productVariants && variantId) {
      const variant = window.productVariants.find(v => v.id == variantId);
      if (variant) {
        basePrice = variant.price / 100;
      }
    }

    if (basePrice === 0 && variantId && variantId !== '') {
      const priceDisplay = productSection?.querySelector('.price-item--regular') || 
                           productSection?.querySelector('.price__container .price-item') ||
                           document.querySelector('.price-item--regular');
      if (priceDisplay) {
        basePrice = parseFloat(priceDisplay.textContent.replace(/[^\d.]/g, '')) || 0;
      }
    }

    let perItemPrice = basePrice;

    // Add addons (per-item) - Look in the whole section as they might be outside the form
    const addonCheckboxes = productSection ? productSection.querySelectorAll('.product-addons__toggle:checked') : this.form.querySelectorAll('.product-addons__toggle:checked');
    
    addonCheckboxes.forEach(cb => {
      const priceEl = cb.closest('.product-addons__item')?.querySelector('.product-addons__current');
      if (priceEl) {
        const addonPrice = parseFloat(priceEl.textContent.replace(/[^\d.]/g, '')) || 0;
        perItemPrice += addonPrice;
      }
    });
    
    let finalTotal = perItemPrice * quantity;

    // FBT slider add-ons selected via "Add to order" (flat, one each per order — getAtcAddonItems
    // sends them at quantity 1 regardless of the parent's quantity, so they must not be multiplied).
    const selectedExtras = (productSection || document).querySelectorAll(
      '.pajama-extras-slider .extraOption:checked'
    );
    selectedExtras.forEach((cb) => {
      const slide = cb.closest('.pajama-extras-slider__slide');
      const priceEl = slide?.querySelector('.pajama-extras-slider__price');
      if (!priceEl) return;
      finalTotal += parseFloat(priceEl.textContent.replace(/[^\d.]/g, '')) || 0;
    });

    // Rush fee (flat, once per order) - Look in the whole section
    const rushCheckbox = productSection ? productSection.querySelector('.extraOption_rush:checked') : 
                         (this.form.querySelector('.extraOption_rush:checked') || document.querySelector('.extraOption_rush:checked'));
                         
    if (rushCheckbox) {
      // Handle both pajamas (.pajamas-rush__price) and standard (.optionPrice) styles
      const rushPriceEl = rushCheckbox.closest('.rushExtraOption-container--pajamas')?.querySelector('[data-creation="rush"] .creation-price--add') ||
                          rushCheckbox.closest('.extraOptionDiv')?.querySelector('.pajamas-rush__price, .optionPrice, .creation-price--add') ||
                          rushCheckbox.closest('label')?.querySelector('.pajamas-rush__price, .optionPrice, .creation-price--add') ||
                          productSection?.querySelector('.pajamas-rush__price, .optionPrice, .creation-price--add');
                          
      if (rushPriceEl) {
        const rushPriceValue = parseFloat(rushPriceEl.textContent.replace(/[^\d.]/g, '')) || 0;
        finalTotal += rushPriceValue;
      }
    }

    const total = finalTotal;
    const totalSpan = document.getElementById('cartTotalDisplay');
    if (totalSpan) {
      totalSpan.textContent = total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
}

class VariantDropdown extends HTMLElement {
  constructor() {
    super();
    this.connected = false;
  }

  connectedCallback() {
    if (this.connected) return;
    this.connected = true;

    const nativeSelect = this.querySelector('.native-style-select');
    if (nativeSelect) {
      nativeSelect.addEventListener('change', (e) => {
        const value = e.target.value;
        if (value) {
          nativeSelect.classList.remove('placeholder-active');
        } else {
          nativeSelect.classList.add('placeholder-active');
        }
        this.setVariantOptions(value);
        if (value) {
          requestAnimationFrame(() => {
            if (surgeIsGallerySyncSuppressed()) {
              surgeResetSplideGalleriesToFirst(
                this.closest('#surge-combined-listing-shell, .main-product-section')
              );
              return;
            }
            surgeSyncGalleryFromVariantPickers(
              this.closest('#surge-combined-listing-shell, .main-product-section')
            );
          });
        }
      });
      return;
    }

    const select = this.querySelector('.custom-select');
    if (!select) return;

    const trigger = select.querySelector('.select-trigger');
    const options = select.querySelectorAll('.option-value');

    if (!trigger || !options.length) return;

    // Preselect initial value from currentSet
    const currentSet = JSON.parse(this.dataset.currentSet || 'null');
    const index = parseInt(this.dataset.optionIndex);
    // Guard: currentSet may be null when Liquid has no selected variant (e.g. all variants sold out)
    if (!Array.isArray(currentSet)) return;
    const currentValue = currentSet[index];
    if (currentValue) {
      const preselectedOption = Array.from(options).find(opt => opt.dataset.value === currentValue);
      if (preselectedOption) {
        this.updateTriggerDisplay(trigger, preselectedOption);
        preselectedOption.classList.add('selected');
      }
    }

    // Toggle dropdown on trigger click
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      select.classList.toggle('open');
    });

    // Handle option selection
    options.forEach((opt) => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        const value = opt.dataset.value;
        if (!value) return;

        // Update trigger display BEFORE closing dropdown to ensure proper rendering
        this.updateTriggerDisplay(trigger, opt);

        select.classList.remove('open');

        options.forEach((o) => o.classList.remove('selected'));
        opt.classList.add('selected');

        // Update variant using same logic as VariantPills
        this.setVariantOptions(value);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!select.contains(e.target)) {
        select.classList.remove('open');
      }
    });
  }

  updateTriggerDisplay(trigger, option) {
    if (!trigger || !option) return;

    // Clear existing content
    trigger.innerHTML = '';

    // Check if option has variant image
    const hasImage = option.dataset.hasImage === 'true';
    const imageUrl = option.dataset.imageUrl;
    const variantImageSwatch = option.querySelector('.variant-image-swatch');

    // Add variant image swatch if available
    if (hasImage && imageUrl) {
      const imageSwatch = document.createElement('span');
      imageSwatch.className = 'dropdown-swatch variant-image-swatch';
      imageSwatch.style.cssText = 'background-image: url(\'' + imageUrl + '\'); background-size: cover; background-position: center; background-repeat: no-repeat; display: inline-block; width: 20px; height: 20px; border-radius: 50%; vertical-align: middle; margin-right: 8px;';
      trigger.appendChild(imageSwatch);
    } else if (variantImageSwatch) {
      // Clone the existing variant image swatch if data attributes are not available
      const clonedSwatch = variantImageSwatch.cloneNode(true);
      trigger.appendChild(clonedSwatch);
    }

    // Get text content only - exclude swatch/icon elements
    let textContent = '';

    // Walk through child nodes and collect text
    for (let i = 0; i < option.childNodes.length; i++) {
      const node = option.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          textContent += (textContent ? ' ' : '') + text;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip swatch and icon elements
        if (!node.classList.contains('dropdown-swatch') &&
          !node.classList.contains('variant-image-swatch') &&
          node.tagName !== 'I') {
          // Get text from this element (but not from nested swatches)
          const elementText = node.textContent.trim();
          if (elementText) {
            textContent += (textContent ? ' ' : '') + elementText;
          }
        }
      }
    }

    // Fallback: use dataset value if no text found
    if (!textContent || textContent.trim() === '') {
      textContent = option.dataset.value || option.textContent.trim() || '';
    }

    // Add text content
    if (textContent && textContent.trim() !== '') {
      const textNode = document.createTextNode(textContent.trim());
      trigger.appendChild(textNode);
    }
  }

  setVariantOptions(value) {
    const siblingMeta = surgeFindCombinedListingSiblingMeta(this, value);
    if (siblingMeta) {
      const targetPath = surgeNormalizeProductPath(siblingMeta.siblingProductUrl);
      const currentPath = surgeNormalizeProductPath(this.dataset.productUrl);
      if (targetPath && targetPath !== currentPath) {
        void surgeSwapCombinedListingShell({
          siblingProductUrl: siblingMeta.siblingProductUrl,
          optionValueId: siblingMeta.optionValueId,
          sectionId: this.dataset.sectionId,
          loaderSelector: this.dataset.loader || null,
          currentProductUrl: this.dataset.productUrl,
          swapTrigger: 'sibling-change'
        });
        return;
      }
    }

    const parent = document.querySelector(this.dataset.parent);
    const mainParent = document.querySelector(this.dataset.mainParent);
    const errElem = document.querySelector(this.dataset.errorSelector);

    if (!parent) return;

    if (mainParent && mainParent.hasAttribute('data-bundle-included')) {
      const bundleCalculator = document.querySelector('bundle-calculator');
      mainParent.removeAttribute('data-bundle-included');
      bundleCalculator.init(null, null, null, null, parseFloat(mainParent.dataset.bundleVariant));
    }

    if (errElem) errElem.classList.add('hidden');

    const currentSet = JSON.parse(this.dataset.currentSet);
    const index = parseInt(this.dataset.optionIndex);
    currentSet[index] = value;

    // Update data-current-set attribute
    this.setAttribute('data-current-set', JSON.stringify(currentSet));

    // Update other variant-pills and variant-dropdowns
    const pills = parent.querySelectorAll('variant-pills');
    pills.forEach(pill => pill.setAttribute('data-current-set', JSON.stringify(currentSet)));

    const dropdowns = parent.querySelectorAll('variant-dropdown');
    dropdowns.forEach(dropdown => dropdown.setAttribute('data-current-set', JSON.stringify(currentSet)));

    let availableSet = null;
    if (this.dataset.checkSets === 'true') {
      const sets = JSON.parse(this.dataset.availableSets);
      const checkSet = (val) => {
        if (!val || !Array.isArray(sets)) return null;
        return sets.find(set => set.includes(val));
      };
      availableSet = checkSet(value);
    }

    onVariantChange(
      this.dataset.jsonSelector,
      availableSet ? availableSet : currentSet,
      this.dataset.forms,
      this.dataset.renders,
      this.dataset.mainParent,
      this.dataset.galleries,
      this.dataset.sectionId,
      this.dataset.productUrl,
      this.dataset.updateUrl,
      this.dataset.focusSelector ? this.dataset.focusSelector : null,
      this.dataset.mediaTemplate === 'true' ? true : false,
      this.dataset.mediaTemplate === 'true' ? this.dataset.template : null,
      this.dataset.loader ? this.dataset.loader : null,
      this.dataset.bundle === 'true' ? true : false
    );
  }

  static get observedAttributes() {
    return ['data-current-set'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-current-set' && oldValue !== newValue && this.connected) {
      const currentSet = JSON.parse(newValue);
      const index = parseInt(this.dataset.optionIndex);
      const currentValue = currentSet[index];

      if (currentValue) {
        const nativeSelect = this.querySelector('.native-style-select');
        if (nativeSelect) {
          const oldSet = oldValue ? JSON.parse(oldValue) : null;
          const oldVal = oldSet ? oldSet[index] : null;

          if ((!oldValue || currentValue === oldVal) && nativeSelect.classList.contains('placeholder-active')) {
            return;
          }

          if (nativeSelect.value !== currentValue) {
            nativeSelect.value = currentValue;
          }
          if (currentValue && nativeSelect.value !== "") {
            nativeSelect.classList.remove('placeholder-active');
          } else {
            nativeSelect.classList.add('placeholder-active');
          }
          if (currentValue && !nativeSelect.classList.contains('placeholder-active')) {
            requestAnimationFrame(() => {
              const scope = this.closest('#surge-combined-listing-shell, .main-product-section');
              if (surgeIsGallerySyncSuppressed()) {
                surgeResetSplideGalleriesToFirst(scope);
                return;
              }
              surgeSyncGalleryFromVariantPickers(scope);
            });
          }
          return;
        }

        const select = this.querySelector('.custom-select');
        const trigger = select?.querySelector('.select-trigger');
        const options = select?.querySelectorAll('.option-value');

        if (trigger && options) {
          const selectedOption = Array.from(options).find(opt => opt.dataset.value === currentValue);
          if (selectedOption) {
            const selectedWithClass = Array.from(options).filter((o) => o.classList.contains('selected'));
            const visuallyAligned =
              selectedOption.classList.contains('selected') &&
              selectedWithClass.length === 1 &&
              selectedWithClass[0] === selectedOption;
            // Avoid clearing/rebuilding the trigger when Liquid + classes already match the new
            // set (updateTriggerDisplay starts with trigger.innerHTML = '' — a common flicker).
            if (!visuallyAligned) {
              this.updateTriggerDisplay(trigger, selectedOption);
              options.forEach((o) => o.classList.remove('selected'));
              selectedOption.classList.add('selected');
            }
          }
        }
      }
    }
  }
}

// Addon Script
(function () {
  function initAddonScript() {
    const addonForm = document.querySelector('form[action^="/cart"]');
    if (addonForm) {
      addonForm.addEventListener('submit', function (e) {
        const selectedAddons = document.querySelectorAll('.product-addons__toggle:checked:not(:disabled)');
        selectedAddons.forEach((checkbox) => {
          const addonItem = checkbox.closest('.product-addons__item');
          if (addonItem && addonItem.classList.contains('product-addons__item--placeholder')) {
            return;
          }
          const variantId = addonItem?.dataset.variantId;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[]';
          input.value = variantId;
          addonForm.appendChild(input);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddonScript);
  } else {
    initAddonScript();
  }
})();

// Legacy theme editor size chart modals (.sizechart-modal); Escape closes.
(function () {
  function closeAllSizecharts() {
    document.querySelectorAll('.sizechart-modal').forEach((modal) => {
      modal.style.display = 'none';
    });
  }

  let sizechartEscapeBound = false;

  function initSizechartModal() {
    document.querySelectorAll('.sizechart-modal').forEach((modal) => {
      if (modal.dataset.sizechartInit === '1') return;
      modal.dataset.sizechartInit = '1';

      const modalContent = modal.querySelector('.sizechart-modal-content');
      const closeBtn = modal.querySelector('.sizechart-close');

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          modal.style.display = 'none';
        });
      }

      modal.addEventListener('click', function (e) {
        if (modalContent && !modalContent.contains(e.target)) {
          modal.style.display = 'none';
        }
      });
    });

    if (!sizechartEscapeBound) {
      sizechartEscapeBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllSizecharts();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSizechartModal);
  } else {
    initSizechartModal();
  }
})();

// Native pajama size-chart trigger (combined listing): open configured-dynamic or static popup reliably.
(function () {
  function getPajamaPopup() {
    return (
      document.getElementById('scModal') ||
      document.getElementById('pajamasSizeChartOpen') ||
      document.querySelector('.popup-chart-container')
    );
  }

  function openDynamicScModalFallback() {
    const primaryRoot = document.querySelector('[data-sc-chart-root][data-sc-primary="true"]');
    const modal = primaryRoot?.querySelector('.sc-modal') || document.querySelector('[data-sc-chart-root] .sc-modal');
    if (!modal) return false;
    modal.hidden = false;
    modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    return true;
  }

  function openPajamaPopup() {
    if (typeof window.openSizeChart === 'function') {
      window.openSizeChart();
      return;
    }
    if (typeof window.handleSizeChartPopupDynamic === 'function') {
      window.handleSizeChartPopupDynamic();
      return;
    }
    const legacyPopup = document.getElementById('pajamasSizeChartOpen');
    if (legacyPopup) {
      legacyPopup.style.display = 'block';
      return;
    }
    if (typeof window.handleSizeChartPopup === 'function') {
      window.handleSizeChartPopup();
      return;
    }
    openDynamicScModalFallback();
  }

  function invokeNativePajamaSizeChart(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    if (typeof window.openSizeChart === 'function') {
      window.openSizeChart();
      return false;
    }
    openPajamaPopup();
    return false;
  }

  window.openNativePajamaSizeChart = invokeNativePajamaSizeChart;

  function closePajamaPopup() {
    if (typeof window.closeScModal === 'function') {
      window.closeScModal();
      return;
    }
    if (typeof window.closePopupDynamic === 'function') {
      window.closePopupDynamic();
      return;
    }
    if (typeof window.closePopup === 'function') {
      window.closePopup();
      return;
    }
    const popup = getPajamaPopup();
    if (popup) popup.style.display = 'none';
  }

  document.addEventListener('click', function (e) {
    const openTrigger =
      e.target.closest('[data-native-size-chart-trigger]') ||
      e.target.closest('.native-pajama-size-chart-trigger .sizeChartLinkText');
    if (openTrigger) {
      e.preventDefault();
      e.stopPropagation();
      invokeNativePajamaSizeChart(e);
      return;
    }

    const closeTrigger = e.target.closest(
      '#scClose, .sc-close-btn, .popup-chart-container .modal-overlay, .popup-chart-container .delete-button span, .popup-chart-container .delete-button button'
    );
    if (closeTrigger) {
      e.preventDefault();
      e.stopPropagation();
      closePajamaPopup();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    const scModal =
      document.querySelector('[data-sc-chart-root][data-sc-primary="true"] .sc-modal') ||
      document.querySelector('[data-sc-chart-root] .sc-modal');
    if (scModal && scModal.classList.contains('open') && !scModal.hidden) {
      closePajamaPopup();
      return;
    }
    const legacyPopup = document.getElementById('pajamasSizeChartOpen');
    if (legacyPopup && legacyPopup.style.display === 'block') {
      closePajamaPopup();
    }
  });
})();


/** Pajama creation-time cards — delegated; survives combined-listing innerHTML swap. */
function surgeInitPajamasCreationTime() {
  if (window.__surgePajamasCreationTimeInit) return;
  window.__surgePajamasCreationTimeInit = true;

  document.addEventListener('click', (e) => {
    const card = e.target?.closest?.('[data-creation]');
    if (!card) return;
    const container = card.closest('.rushExtraOption-container--pajamas');
    if (!container?.contains(card)) return;

    const stdCard = container.querySelector('[data-creation="standard"]');
    const rushCard = container.querySelector('[data-creation="rush"]');
    const rushInput = container.querySelector('input.extraOption[data-identifier="rush"]');
    if (!stdCard || !rushCard || !rushInput) return;

    const mode = card.getAttribute('data-creation');
    if (mode === 'standard') {
      rushInput.checked = false;
      stdCard.classList.add('active');
      rushCard.classList.remove('active');
    } else if (mode === 'rush') {
      rushInput.checked = true;
      rushCard.classList.add('active');
      stdCard.classList.remove('active');
    } else return;

    rushInput.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

document.addEventListener("DOMContentLoaded", (event) => {
  surgeInitPajamasCreationTime();

  customElements.define('variant-pills', VariantPills);
  customElements.define('variant-dropdown', VariantDropdown);
  customElements.define('pac-section', PacSection);

  if (!customElements.get('gift-block')) customElements.define('gift-block', GiftBlock);
  if (!customElements.get('atc-submit')) customElements.define('atc-submit', AtcSubmit);
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  const surgeAutoSelectDefaultColorForParent = () => {
    const barPath = window.location.pathname;
    const hasOptionValues = window.location.search.includes('option_values');
    const hasBackgroundParam = window.location.search.includes('background=');
    if (barPath.includes('/products/custom-pet-pajamas') && !hasOptionValues && !hasBackgroundParam) {
      setTimeout(() => {
        const targetColor = "Light Blue Paws";
        const swatch = document.querySelector(`.variant-options-holder input[aria-label="${targetColor}"], .variant-options-holder label[data-option-name="${targetColor}"]`);
        if (swatch) {
          const label = swatch.tagName === 'LABEL' ? swatch : swatch.nextElementSibling;
          if (label && label.tagName === 'LABEL') {
            label.click();
          } else if (swatch.tagName === 'INPUT') {
            swatch.click();
          }
        }
      }, 2000);
    }
  };

  const hydrateGallery = (shellSwapped) => {
    const shell = document.getElementById('surge-combined-listing-shell');
    const clHost = shell?.querySelector('[data-combined-sibling-option="true"]');
    if (!clHost) {
      surgeSyncInitialVariantGallery();
      return;
    }
    let hasOptionValues = false;
    try {
      hasOptionValues = !!new URLSearchParams(window.location.search).get('option_values');
    } catch {
      hasOptionValues = false;
    }
    // When the shopper arrived from a collection card for a specific color (`?option_values=`)
    // but the child-shell swap did NOT land (id mismatch, guarded off, or fetch failure),
    // forcing the gallery to slide 0 shows the parent's first color (e.g. "Pink Dream") — the
    // reported "flash then reset" bug. In that case sync to the server-selected variant's
    // featured media (which honors the URL param) instead of resetting to the first slide.
    if (hasOptionValues && !shellSwapped) {
      surgeSyncInitialVariantGallery();
    } else {
      surgeResetSplideGalleriesToFirst(shell);
    }
  };
  const runCombinedListingHydrate = async () => {
    let shellSwapped = false;
    try {
      shellSwapped = (await surgeMaybeSwapCombinedListingShellOnLoad()) === true;
    } catch (e) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[combined-listings] initial shell hydrate skipped', e);
      }
    }
    surgeScheduleCombinedListingSiblingPrefetch();
    surgeInitCombinedListingPrefetchPointer();
    surgeAutoSelectDefaultColorForParent();
    surgeRefreshPajamaExtrasBandanaValues();
    requestAnimationFrame(() => {
      hydrateGallery(shellSwapped);
      // Correct color is now in the gallery (child swap applied, or synced to the selected
      // variant) — clear the initial-load mask so the gallery fades in without a Pink Dream flash.
      document
        .getElementById('surge-combined-listing-shell')
        ?.classList.remove('surge-cl-initial-mask');
      setTimeout(() => hydrateGallery(shellSwapped), 50);
      setTimeout(() => hydrateGallery(shellSwapped), 250);
    });
  };
  requestAnimationFrame(() => {
    void runCombinedListingHydrate();
  });

  surgeRefreshPajamaExtrasBandanaValues();
  surgeInitPdpPriceRowSync();
  surgeInitVariantGallerySyncListeners();

})